import React, { useEffect, useMemo, useRef, useState } from "react";
import { isPolicyOverrideAttendance, normalizePolicyRecord } from "../../lib/attendancePolicyConfig";
import { createPortal } from "react-dom";
import { Info, MoreVertical, Printer, Settings, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { printAllPoliciesAsDocument, printSinglePolicyAsDocument } from "./policySummaryPrint";
import {
  buildAttendancePolicyDocumentHtml,
  clonePolicySnapshot,
  computeVersionOnSave,
  getPolicySnapshotsForDisplay,
  mergePolicySnapshots,
  printAttendancePolicyDocumentHtml,
} from "./attendancePolicyDocument";
import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";
import { dispatchPolicyUpdated, isDemoAttendancePolicy } from "../../lib/attendanceDemoSeed";
import PolicyFormWizardShell from "./policy-wizard/PolicyFormWizardShell";
import {
  getAttendancePolicyWizardCurrentId,
  getAttendancePolicyWizardSteps,
  getNextAttendancePolicyFormStep,
  getPrevAttendancePolicyFormStep,
  getAttendancePolicyNextButtonLabel,
  isAttendancePolicyOverrideMode,
  mapAttendancePolicyWizardClickToFormStep,
} from "./policy-wizard/policyWizardSteps";
import usePolicyCompanyRoles from "./policy-wizard/usePolicyCompanyRoles";
import PolicyRoleSelectOptions from "./policy-wizard/PolicyRoleSelectOptions";
import {
  getRoleLabel,
  normalizeRoleCodeForMatch,
  resolveRoleSelectValue,
} from "../../utils/roleSelectOptions";

import { attendanceStructureService } from "../../services/api";
import { cutOffExampleText } from "../../lib/attendanceCutoff";
import { defaultActivityWorkflows } from "../../lib/attendanceActivityWorkflows";
import ActivityWorkflowsPolicySection from "./attendance/components/ActivityWorkflowsPolicySection";

/** Org-hierarchy step (not an RBAC role) — kept alongside User Role Management roles. */
const REPORTING_MANAGER_OPTION = {
  value: "reporting_manager",
  label: "Reporting Manager",
  isSystem: true,
  id: "structural_reporting_manager",
};

/** Section title with optional info tooltip (matches header text size). */
function PolicySectionHeading({ title, tip, className = "" }) {
  return (
    <div className={`mb-3 flex items-center gap-1.5 ${className}`}>
      <h4 className="text-sm font-semibold leading-none text-[hsl(var(--ds-fg))]">{title}</h4>
      {tip ? (
        <span
          title={tip}
          className="inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center text-[hsl(var(--ds-muted-fg))]"
          aria-label={tip}
        >
          <Info size={14} strokeWidth={1.75} className="h-3.5 w-3.5" />
        </span>
      ) : null}
    </div>
  );
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const STORAGE = {
  policies: "hrms_frontend_attendance_policies_v1",
  shifts: "hrms_frontend_attendance_shifts_v1",
  leaveGroups: "hrms_frontend_attendance_leave_groups_v1",
  weeklyOffGroups: "hrms_frontend_attendance_weekly_off_groups_v1",
  holidayGroups: "hrms_frontend_attendance_holiday_groups_v1",
};

const SHIFT_PENALTY_TYPES = [
  { value: "none", label: "None" },
  { value: "minutes_lop", label: "LOP (deduct minutes)" },
  { value: "half_day", label: "Treat as half-day absence" },
  { value: "full_day", label: "Treat as full-day absence" },
];

function normalizeShiftWorkflowStep(s = {}) {
  return {
    role: s.role || "manager",
    slaDays: Number(s.slaDays) || 3,
    notifyOnly: !!s.notifyOnly,
  };
}

function defaultShiftExtras() {
  return {
    isDefault: false,
    status: "active",
    shiftType: "fixed",
    expectedWorkHours: 8,
    breakHandling: "deduct_from_work_hours",
    totalBreakMinutes: 60,
    paidBreakMinutes: 0,
    unpaidBreakMinutes: 60,
    flexiEnabled: false,
    flexiBandwidthStart: "08:00",
    flexiBandwidthEnd: "11:00",
    coreHoursStart: "11:00",
    coreHoursEnd: "17:00",
    graceCheckInMinutes: 15,
    graceCheckOutMinutes: 15,
    graceRulesEnabled: true,
    graceOccurrenceWindow: "monthly",
    lateGraceAllowedOccurrences: 0,
    earlyGraceAllowedOccurrences: 0,
    earlyLoginBufferMinutes: 0,
    lateLogoutBufferMinutes: 0,
    crossDayShift: false,
    considerLateAfterMinutes: 15,
    considerEarlyBeforeMinutes: 30,
    fullDayHours: 8,
    halfDayHours: 4,
    fullDayIfHoursAtLeast: 8,
    halfDayIfHoursLessThan: 4,
    latePenaltyType: "none",
    latePenaltyMinutes: 0,
    earlyDeparturePenaltyType: "none",
    earlyDeparturePenaltyMinutes: 0,
    shiftRotationEnabled: false,
    shiftRotationPattern: "weekly",
    shiftRotationRequiresApproval: false,
    allowShiftOverride: false,
    shiftOverrideRequiresApproval: false,
    shiftOverrideWorkflowSteps: [],
  };
}

function normalizeShift(raw) {
  const base = defaultShiftExtras();
  if (!raw || typeof raw !== "object") {
    return { id: "x", name: "—", startTime: "09:00", endTime: "18:00", breakMinutes: 60, ...base };
  }
  const wfs = Array.isArray(raw.shiftOverrideWorkflowSteps)
    ? raw.shiftOverrideWorkflowSteps.map(normalizeShiftWorkflowStep)
    : [];
  const merged = {
    ...base,
    ...raw,
    shiftOverrideWorkflowSteps: wfs,
  };
  if (merged.totalBreakMinutes == null) {
    merged.totalBreakMinutes = Math.max(
      0,
      Number(merged.paidBreakMinutes || 0) + Number(merged.unpaidBreakMinutes || merged.breakMinutes || 0)
    );
  }
  return merged;
}

function emptyCreateShiftForm() {
  return {
    name: "",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
    ...defaultShiftExtras(),
    shiftOverrideWorkflowSteps: [],
  };
}

const defaultShifts = [
  normalizeShift({
    id: "general",
    name: "General",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
    isDefault: true,
  }),
  normalizeShift({
    id: "morning",
    name: "Morning (9–6)",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
  }),
  normalizeShift({
    id: "night",
    name: "Night",
    startTime: "22:00",
    endTime: "06:00",
    breakMinutes: 30,
  }),
  normalizeShift({
    id: "flexi",
    name: "Flexi",
    startTime: "08:00",
    endTime: "20:00",
    breakMinutes: 0,
  }),
];
const defaultLeaveGroups = [
  { id: "default", name: "Default Leave Group", description: "" },
  { id: "india", name: "India", description: "" },
];
const defaultWeeklyOffGroups = [
  { id: "sat_sun", name: "Saturday–Sunday", offDays: [6, 0] },
  { id: "fri_sat", name: "Friday–Saturday", offDays: [5, 6] },
];
const defaultHolidayGroups = [
  { id: "india", name: "India Holidays", holidays: [] },
  { id: "state", name: "State Holidays", holidays: [] },
];

const CONDITION_TYPES = [
  { value: "leave_type", label: "Leave type is" },
  { value: "leave_days_above", label: "Leave days above" },
  { value: "leave_days_below", label: "Leave days below" },
];

function loadFromStorage(key, def) {
  try {
    const s = localStorage.getItem(key);
    if (s) {
      const p = JSON.parse(s);
      if (Array.isArray(p)) return p;
    }
  } catch {
    /* ignore parse / storage errors */
  }
  return def;
}
function saveToStorage(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
    if (key === STORAGE.policies || key === STORAGE.shifts) {
      dispatchPolicyUpdated();
    }
  } catch {
    /* ignore quota / storage errors */
  }
}

function emptyPolicyForm() {
  return {
    policyName: "",
    effectiveDate: new Date().toISOString().slice(0, 10),
    autoAssignOnJoin: false,
    autoAttendance: false,
    /** ESS: allow edit/apply for day status when Auto attendance is on (punch-based = always off). */
    employeeStatusEditEnabled: false,
    /** When employeeStatusEditEnabled: true = approval workflow; false = direct edit. */
    employeeStatusEditRequiresWorkflow: true,
    checkInMobile: true,
    checkInWeb: true,
    checkInBiometric: false,
    trackLocation: false,
    restrictLocationForOnlineCheckIn: false,
    allowedLocations: [],
    graceCheckInMinutes: 15,
    graceCheckOutMinutes: 15,
    considerLateAfterMinutes: 15,
    considerEarlyBeforeMinutes: 30,
    fullDayHours: 8,
    halfDayHours: 4,
    fullDayIfHoursAtLeast: 8,
    halfDayIfHoursLessThan: 4,
    regularizationAllowed: true,
    shortLeaveEnabled: false,
    shortLeaveMaxMinutes: 60,
    changeShiftEnabled: true,
    regularizationTillDays: 7,
    cutOffMethod: "calendar_month",
    cutOffFromDay: 26,
    cutOffToDay: 25,
    cutOffEnabled: false,
    cutOffDay: 25,
    cutOffNewJoinerHandling: "defer_lop",
    cutOffSeparationHandling: "fnf_recovery",
    cutOffExcessPayRecovery: true,
    shiftGroupId: "",
    leaveGroupId: "",
    holidayGroupId: "",
    weeklyOffGroupId: "",
    branch: "",
    department: "",
    designation: "",
    grade: "",
    category: "",
    subDepartment: "",
    location: "",
    individualEmployeeIds: "",
    approvalMethod: "auto_approved",
    reminderDays: 7,
    needWorkflowSteps: false,
    workflowSteps: [],
    workflowParallelApproval: false,
    workflowEscalationAfterDays: 3,
    workflowAutoApprovalIfNoAction: false,
    workflowRejectionWithReason: true,
    workflowReinitiationAllowed: true,
    conditionalWorkflowRules: [],
    allEmployees: false,
    employmentTypes: [],
    sandwichRuleEnabled: false,
    sandwichRuleWeeklyOff: true,
    sandwichRuleHoliday: true,
    sandwichRuleNotes: "",
    compensatoryOffEnabled: false,
    compensatoryGeneralConditions: "",
    otCompOffMode: "none",
    otMinHoursPerDay: 1,
    otPayMultiplier: 1.5,
    compOffMinHours: 4,
    compOffEligiblePresent: true,
    compOffEligibleWeekOffWorked: true,
    compOffEligibleHolidayWorked: true,
    compOffExpiryDays: 90,
    compOffRequiresApproval: false,
    regularizationLockedMonthsMax: 2,
    regularizationMaxCalendarDaysBack: 90,
    regularizationMaxPunchCorrectionsPerMonth: 10,
    regularizationMaxRequestInstancesPerMonth: 5,
    attendanceFreezeDaysBeforeMonthEnd: 0,
    missedPunchReminderAfterHours: 4,
    // Attendance capture validation controls
    checkInManual: false,
    enableGeoFencing: false,
    geoFenceRadiusMeters: 100,
    enforceIpRestriction: false,
    allowedIpRanges: "",
    enforceDeviceBinding: false,
    maxBoundDevicesPerEmployee: 1,
    duplicatePunchControlEnabled: true,
    duplicatePunchWindowMinutes: 2,
    // Punch mode and missing punch behavior
    punchMode: "double",
    checkInOutRule: "first_in_last_out",
    overrideAttendance: false,
    autoLogoutEnabled: false,
    autoLogoutMode: "shift_end_time",
    autoLogoutAfterHours: 9,
    autoLogoutTime: "23:59",
    missingPunchHandling: "regularization_required",
    defaultMarkingForMissingPunch: "half_day",
    // Hours calculation and break logic
    workHoursCalcMethod: "first_in_last_out",
    breakDeductionMethod: "fixed",
    breakFixedMinutes: 60,
    breakPunchPairingEnabled: false,
    workingHoursRoundingUnit: "15",
    workingHoursRoundingDirection: "nearest",
    graceRulesEnabled: true,
    graceOccurrenceWindow: "monthly",
    lateGraceAllowedOccurrences: 0,
    earlyGraceAllowedOccurrences: 0,
    latePenaltyType: "none",
    latePenaltyMinutes: 0,
    earlyDeparturePenaltyType: "none",
    earlyDeparturePenaltyMinutes: 0,
    // Late / early frequency and penalty
    lateAllowedOccurrencesPerMonth: 3,
    earlyExitAllowedOccurrencesPerMonth: 3,
    latePenaltyAction: "none",
    earlyExitPenaltyAction: "none",
    // OT policy expansion
    overtimeEnabled: false,
    overtimeRequiresPreApproval: false,
    overtimeMinThresholdMinutes: 30,
    overtimeRateMultiplier: 1.5,
    overtimeDailyCapHours: 4,
    overtimeMonthlyCapHours: 60,
    overtimeApplicableEmploymentTypes: [],
    // Comp-off expansion
    compOffAccrualUnitHours: 1,
    compOffMaxAccrualDaysPerMonth: 2,
    compOffEncashmentEnabled: false,
    compOffEncashmentRate: 1,
    // Leave-attendance integration
    leaveAttendancePriorityRule: "approved_leave_first",
    halfDayLeaveMappingEnabled: true,
    // Auto marking and exceptions
    autoAbsentMarkingEnabled: true,
    absentMarkIfHoursBelow: 2,
    autoHalfDayMarkingEnabled: true,
    halfDayMarkIfHoursBelow: 4,
    excludeOnDutyFromAutoMarking: true,
    excludeWfhFromAutoMarking: true,
    // Regularization workflow controls
    regularizationCutOffDayOfMonth: 5,
    regularizationApprovalLevels: 2,
    regularizationRequireReason: true,
    // OD / WFH
    odRequestEnabled: true,
    wfhRequestEnabled: true,
    odWfhRequireApproval: true,
    odWfhTrackingMode: "hours",
    // Deviations and penalties
    penaltyEngineEnabled: false,
    penaltyResetCycle: "monthly",
    penaltyRuleNotes: "",
    // Payroll outputs and lock
    payrollIntegrationEnabled: true,
    payrollExportIncludePresentDays: true,
    payrollExportIncludePaidDays: true,
    payrollExportIncludeOtHours: true,
    payrollExportIncludeLopDays: true,
    payrollLockEnabled: true,
    payrollLockDayOfMonth: 2,
    // Notifications / escalation
    notificationsEnabled: true,
    notifyMissedPunch: true,
    notifyLateComing: true,
    notifyEarlyExit: true,
    notifyRegularizationPending: true,
    escalationEnabled: false,
    escalationAfterHours: 24,
    // Reports and compliance
    dashboardMusterRollEnabled: true,
    dashboardExceptionReportEnabled: true,
    dashboardAttendanceLogEnabled: true,
    complianceWorkingHoursCapPerDay: 12,
    complianceWeeklyHoursCap: 60,
    complianceOvertimeRequiresConsent: true,
    // System controls
    attendanceLockEnabled: true,
    attendanceLockAfterDays: 3,
    auditTrailEnabled: true,
    bulkUploadEnabled: true,
    apiIntegrationEnabled: false,
    apiIntegrationNotes: "",
    punchDayMode: "auto",
    minHoursBetweenShifts: 8,
    dayEndCheckHours: 8,
    overnightOutAttachesToInDay: true,
    missingAttendanceAction: "lop",
    compOffCreditMode: "post",
    overtimeAutoApproveToPayroll: false,
    activityWorkflows: defaultActivityWorkflows(),
  };
}

const portalToBody = (overlay) =>
  overlay ? createPortal(overlay, document.body) : null;

const AttendancePolicyComponent = ({
  darkMode,
  branchesList,
  departmentsList,
  designationsList,
  subDepartmentsList,
  gradesList,
  categoriesList,
  mode = "policy",
  companyName = MASTERS_DOCUMENT_COMPANY_NAME,
  companyAddress = MASTERS_DOCUMENT_COMPANY_ADDRESS,
  policyPreparedBy = "Human Resources",
  policyApprovedBy = "Management",
}) => {
  const { policyRoleOptions, defaultPolicyRoleValue } = usePolicyCompanyRoles();

  /** Company roles from User Role Management + Reporting Manager (org link). */
  const WORKFLOW_ROLES = useMemo(() => {
    const hasRm = policyRoleOptions.some(
      (r) => normalizeRoleCodeForMatch(r.value) === "reporting_manager"
    );
    return hasRm ? policyRoleOptions : [REPORTING_MANAGER_OPTION, ...policyRoleOptions];
  }, [policyRoleOptions]);

  const APPROVAL_ROLES = WORKFLOW_ROLES;
  const defaultWorkflowRole = defaultPolicyRoleValue || WORKFLOW_ROLES[0]?.value || "reporting_manager";

  const LOCATION_OPTIONS = [
    { value: "", label: "All Locations" },
    { value: "1", label: "Mumbai" },
    { value: "2", label: "Delhi" },
    { value: "3", label: "Bangalore" },
  ];
  const EMPLOYMENT_TYPE_OPTIONS = [
    { value: "full_time", label: "Full-time" },
    { value: "contract", label: "Contract" },
    { value: "intern", label: "Intern" },
    { value: "consultant", label: "Consultant" },
    { value: "part_time", label: "Part-time" },
    { value: "trainee", label: "Trainee" },
  ];
  const ORG_APPLICABILITY_KEYS = [
    "branch",
    "department",
    "designation",
    "grade",
    "category",
    "subDepartment",
    "location",
  ];

  const docCompanyName =
    (typeof companyName === "string" && companyName.trim()) || MASTERS_DOCUMENT_COMPANY_NAME;
  const docCompanyAddress =
    (typeof companyAddress === "string" && companyAddress.trim()) || MASTERS_DOCUMENT_COMPANY_ADDRESS;

  const [policies, setPolicies] = useState(() => loadFromStorage(STORAGE.policies, []));
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesSyncError, setPoliciesSyncError] = useState(null);
  const [shifts, setShifts] = useState(() => loadFromStorage(STORAGE.shifts, defaultShifts));
  const [leaveGroups, setLeaveGroups] = useState(() => loadFromStorage(STORAGE.leaveGroups, defaultLeaveGroups));
  const [weeklyOffGroups, setWeeklyOffGroups] = useState(() =>
    loadFromStorage(STORAGE.weeklyOffGroups, defaultWeeklyOffGroups)
  );
  const [holidayGroups, setHolidayGroups] = useState(() =>
    loadFromStorage(STORAGE.holidayGroups, defaultHolidayGroups)
  );

  /** Load company policies from backend; migrate any legacy browser-only rows once. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPoliciesLoading(true);
      setPoliciesSyncError(null);
      try {
        const res = await attendanceStructureService.getAttendancePolicies();
        if (cancelled) return;
        const remote = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const normalizedRemote = remote.map((p) => normalizePolicyRecord(p));
        if (normalizedRemote.length > 0) {
          setPolicies(normalizedRemote);
          return;
        }
        const local = loadFromStorage(STORAGE.policies, []);
        if (!local.length) {
          setPolicies([]);
          return;
        }
        // One-time migrate browser-only policies into the company DB
        const migrated = [];
        for (const p of local) {
          try {
            const created = await attendanceStructureService.createAttendancePolicy({
              ...p,
              name: p.policyName || p.name,
              policyName: p.policyName || p.name,
              effectiveDate: p.effectiveDate || null,
              status: p.status || "active",
            });
            const row = created?.data || created;
            if (row) migrated.push(normalizePolicyRecord(row));
          } catch (e) {
            console.warn("Failed to migrate attendance policy", p?.policyName, e);
          }
        }
        if (!cancelled) {
          setPolicies(migrated.length ? migrated : local.map(normalizePolicyRecord));
          if (migrated.length) {
            toast.success(`Migrated ${migrated.length} attendance policy(ies) to server`);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setPoliciesSyncError(
            e?.response?.data?.message || e?.message || "Could not load policies from server"
          );
          // Keep localStorage fallback so the screen still works offline
          setPolicies(loadFromStorage(STORAGE.policies, []).map(normalizePolicyRecord));
        }
      } finally {
        if (!cancelled) setPoliciesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Load company shifts from backend; migrate any legacy browser-only rows once. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await attendanceStructureService.getAttendanceShifts();
        if (cancelled) return;
        const remote = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const normalizedRemote = remote.map((s) => normalizeShift(s));
        if (normalizedRemote.length > 0) {
          setShifts(normalizedRemote);
          return;
        }
        const local = loadFromStorage(STORAGE.shifts, defaultShifts);
        const toMigrate = (local.length ? local : defaultShifts).map((s) => normalizeShift(s));
        if (!toMigrate.length) {
          setShifts([]);
          return;
        }
        const migrated = [];
        for (const s of toMigrate) {
          try {
            const created = await attendanceStructureService.createAttendanceShift(s);
            const row = created?.data || created;
            if (row) migrated.push(normalizeShift(row));
          } catch (e) {
            console.warn("Failed to migrate attendance shift", s?.name, e);
          }
        }
        if (!cancelled) {
          setShifts(migrated.length ? migrated : toMigrate);
          if (migrated.length) {
            toast.success(`Migrated ${migrated.length} shift(s) to server`);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setShifts(loadFromStorage(STORAGE.shifts, defaultShifts).map((s) => normalizeShift(s)));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    saveToStorage(STORAGE.policies, policies);
  }, [policies]);
  useEffect(() => {
    saveToStorage(STORAGE.shifts, shifts);
  }, [shifts]);
  useEffect(() => {
    saveToStorage(STORAGE.leaveGroups, leaveGroups);
  }, [leaveGroups]);
  useEffect(() => {
    saveToStorage(STORAGE.weeklyOffGroups, weeklyOffGroups);
  }, [weeklyOffGroups]);
  useEffect(() => {
    saveToStorage(STORAGE.holidayGroups, holidayGroups);
  }, [holidayGroups]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [policyFormStep, setPolicyFormStep] = useState(1);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState(null);
  const [showInactivePolicies, setShowInactivePolicies] = useState(false);
  const [policySearch, setPolicySearch] = useState("");
  const [policyHelpOpen, setPolicyHelpOpen] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [saveGateModal, setSaveGateModal] = useState(null);
  const [policySaveError, setPolicySaveError] = useState(null);
  const [approvalSettingsOpen, setApprovalSettingsOpen] = useState(false);
  const [approvalRolesForPolicy, setApprovalRolesForPolicy] = useState(["management", "ceo"]);
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [flowchartPolicy, setFlowchartPolicy] = useState(null);
  const [policyDocModal, setPolicyDocModal] = useState(null);
  const [policyHistoryModalId, setPolicyHistoryModalId] = useState(null);
  const [historyPreviewEntry, setHistoryPreviewEntry] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [shiftActionMenu, setShiftActionMenu] = useState(null);
  const [showInactiveShifts, setShowInactiveShifts] = useState(false);
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [createModal, setCreateModal] = useState(null);
  const [createShiftForm, setCreateShiftForm] = useState(() => ({
    name: "",
    startTime: "09:00",
    endTime: "18:00",
    breakMinutes: 60,
    ...defaultShiftExtras(),
    shiftOverrideWorkflowSteps: [],
  }));
  const [createLeaveForm, setCreateLeaveForm] = useState({ name: "", description: "" });
  const [createWeeklyOffForm, setCreateWeeklyOffForm] = useState({ name: "", offDays: [] });
  const [createHolidayForm, setCreateHolidayForm] = useState({
    name: "",
    holidays: [{ date: "", name: "" }],
  });

  const policyHelpRef = useRef(null);
  const approvalSettingsRef = useRef(null);

  const normalizeWorkflowStep = (s = {}) => ({
    role: s.role || "manager",
    slaDays: Number(s.slaDays) || 3,
    notifyOnly: !!s.notifyOnly,
  });

  const createDefaultPolicy = () => ({
    ...emptyPolicyForm(),
    policyVersion: 1,
    policyVersionHistory: [],
    policySnapshots: [],
    policyDocumentPushed: false,
    policyPushedAt: null,
    status: "active",
  });

  const [newPolicy, setNewPolicy] = useState(createDefaultPolicy());

  const policyDocumentOptions = useMemo(
    () => ({
      companyName: docCompanyName,
      companyAddress: docCompanyAddress,
      preparedBy: policyPreparedBy,
      approvedBy: policyApprovedBy,
      workflowRoleLabel: (role) => getRoleLabel(WORKFLOW_ROLES, role),
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
      locationOptions: LOCATION_OPTIONS,
      shifts,
      leaveGroups,
      holidayGroups,
      weeklyOffGroups,
    }),
    [
      docCompanyName,
      docCompanyAddress,
      policyPreparedBy,
      policyApprovedBy,
      branchesList,
      departmentsList,
      designationsList,
      gradesList,
      categoriesList,
      subDepartmentsList,
      shifts,
      leaveGroups,
      holidayGroups,
      weeklyOffGroups,
    ]
  );

  const POLICY_SIGNATURE_OMIT = new Set([
    "id",
    "createdAt",
    "updatedAt",
    "policySnapshots",
    "policyVersionHistory",
    "policyDocumentPushed",
    "policyPushedAt",
    "policyVersion",
    "effectiveDate",
    "status",
  ]);

  const policyContentSignature = (p) => {
    if (!p) return "";
    const o = {};
    Object.keys(p)
      .sort()
      .forEach((k) => {
        if (POLICY_SIGNATURE_OMIT.has(k)) return;
        let v = p[k];
        if (k === "employmentTypes") v = Array.isArray(v) ? [...v].map(String).sort() : [];
        o[k] = v;
      });
    return JSON.stringify(o);
  };

  const parseIndividualIds = (policy) => {
    const s = policy.individualEmployeeIds;
    if (Array.isArray(s)) return s.map(String).filter(Boolean);
    return String(s || "")
      .split(/[\s,]+/)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const normalizeEmploymentTypesArr = (policy) => {
    const a = policy.employmentTypes;
    if (!Array.isArray(a) || a.length === 0) return null;
    return [...new Set(a.map(String))].sort();
  };

  const scopesApplicabilityOverlap = (a, b) => {
    if (String(a.id) === String(b.id)) return false;
    const aIds = parseIndividualIds(a);
    const bIds = parseIndividualIds(b);
    const aAll = !!a.allEmployees;
    const bAll = !!b.allEmployees;
    const aHasOrg = ORG_APPLICABILITY_KEYS.some((k) => a[k]);
    const bHasOrg = ORG_APPLICABILITY_KEYS.some((k) => b[k]);
    if (!aAll && !bAll && !aHasOrg && !bHasOrg && aIds.length && bIds.length) {
      const setB = new Set(bIds);
      return aIds.some((id) => setB.has(id));
    }
    if (aAll || bAll) {
      const aEm = normalizeEmploymentTypesArr(a);
      const bEm = normalizeEmploymentTypesArr(b);
      if (aEm && bEm) {
        const inter = aEm.filter((t) => bEm.includes(t));
        if (inter.length === 0) return false;
      }
      return true;
    }
    for (const k of ORG_APPLICABILITY_KEYS) {
      const va = a[k] ? String(a[k]) : "";
      const vb = b[k] ? String(b[k]) : "";
      if (va && vb && va !== vb) return false;
    }
    const aEm = normalizeEmploymentTypesArr(a);
    const bEm = normalizeEmploymentTypesArr(b);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length === 0) return false;
    }
    if (aIds.length && bIds.length) {
      const setB = new Set(bIds);
      return aIds.some((id) => setB.has(id));
    }
    if ((aIds.length && (bHasOrg || bAll)) || (bIds.length && (aHasOrg || aAll))) return true;
    if (aIds.length || bIds.length) return true;
    return true;
  };

  const findConflictingPolicies = (candidate, list, excludeId) =>
    list.filter((p) => {
      if (p.status === "inactive") return false;
      if (excludeId && String(p.id) === String(excludeId)) return false;
      return scopesApplicabilityOverlap(candidate, p);
    });

  const masterName = (list, id) =>
    (list || []).find((x) => String(x.id) === String(id))?.name || "";

  const summarizeOverlapReason = (candidate, other) => {
    const bits = [];
    if (candidate.allEmployees || other.allEmployees) {
      bits.push(
        'One policy is set to "All employees". That scope overlaps with other applicability rules.'
      );
    }
    for (const k of ORG_APPLICABILITY_KEYS) {
      const va = candidate[k] ? String(candidate[k]) : "";
      const vb = other[k] ? String(other[k]) : "";
      if (va && vb && va === vb) {
        const label =
          k === "branch"
            ? "Branch"
            : k === "department"
              ? "Department"
              : k === "designation"
                ? "Designation"
                : k === "grade"
                  ? "Grade"
                  : k === "category"
                    ? "Category"
                    : k === "subDepartment"
                      ? "Sub-department"
                      : "Location";
        const list =
          k === "branch"
            ? branchesList
            : k === "department"
              ? departmentsList
              : k === "designation"
                ? designationsList
                : k === "grade"
                  ? gradesList
                  : k === "category"
                    ? categoriesList
                    : k === "subDepartment"
                      ? subDepartmentsList
                      : null;
        const nm = list ? masterName(list, va) : va;
        bits.push(`Same ${label}: "${nm || va}".`);
      }
    }
    const aEm = normalizeEmploymentTypesArr(candidate);
    const bEm = normalizeEmploymentTypesArr(other);
    if (aEm && bEm) {
      const inter = aEm.filter((t) => bEm.includes(t));
      if (inter.length) {
        bits.push(
          `Overlapping employment type(s): ${inter
            .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
            .join(", ")}.`
        );
      }
    }
    if (!bits.length) {
      bits.push("Applicability may overlap — review scope before saving.");
    }
    return bits.join(" ");
  };

  const getPolicySummaryRows = (p) => {
    const indIds = Array.isArray(p.individualEmployeeIds)
      ? p.individualEmployeeIds.join(", ")
      : p.individualEmployeeIds || "";
    const appLabel = (list, value) => {
      if (!value) return "All";
      const found = (list || []).find((x) => String(x.id) === String(value));
      return found?.name || (value ? "—" : "All");
    };
    const rows = [];
    const yesNo = (v) => (v ? "Yes" : "No");
    const pushApplicabilityRows = () => {
      rows.push({ label: "All employees (applicability)", value: p.allEmployees ? "Yes" : "No" });
      rows.push({
        label: "Employment types",
        value:
          Array.isArray(p.employmentTypes) && p.employmentTypes.length
            ? p.employmentTypes
                .map((v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v)
                .join(", ")
            : "All types",
      });
      rows.push({ label: "Branch", value: appLabel(branchesList, p.branch) });
      rows.push({ label: "Department", value: appLabel(departmentsList, p.department) });
      rows.push({ label: "Designation", value: appLabel(designationsList, p.designation) });
      rows.push({ label: "Grade", value: appLabel(gradesList, p.grade) });
      rows.push({ label: "Category", value: appLabel(categoriesList, p.category) });
      rows.push({ label: "Sub-department", value: appLabel(subDepartmentsList, p.subDepartment) });
      rows.push({
        label: "Location",
        value: LOCATION_OPTIONS.find((o) => o.value === (p.location || ""))?.label || "All",
      });
      rows.push({
        label: "Individual employees",
        value: (typeof indIds === "string" && indIds.trim()) || "—",
      });
    };

    rows.push({
      label: "Policy name",
      value: typeof p.policyName === "string" ? p.policyName.trim() || p.policyName : p.policyName || "—",
    });
    rows.push({ label: "Effective date", value: p.effectiveDate || "—" });
    rows.push({
      label: "Policy document version",
      value: p.policyVersion != null ? String(p.policyVersion) : "1",
    });
    rows.push({ label: "Auto-assign on joining", value: p.autoAssignOnJoin ? "Yes" : "No" });
    rows.push({
      label: "Auto attendance (all days present)",
      value: p.autoAttendance ? "Yes" : "No",
    });
    rows.push({
      label: "Override Attendance (manual marking)",
      value: isPolicyOverrideAttendance(p) ? "Yes" : "No",
    });
    rows.push({
      label: "ESS — Edit Attendance Status",
      value: !p.autoAttendance
        ? "No (punch-based)"
        : p.employeeStatusEditEnabled
        ? p.employeeStatusEditRequiresWorkflow !== false
          ? "Apply with workflow"
          : "Direct edit"
        : "No",
    });
    rows.push({
      label: "Attendance cut-off (LOP deferral)",
      value: p.cutOffEnabled
        ? `Yes — day ${p.cutOffDay ?? p.cutOffToDay ?? 25}; joiner: ${
            p.cutOffNewJoinerHandling || "defer_lop"
          }; separation: ${p.cutOffSeparationHandling || "fnf_recovery"}`
        : "No",
    });

    // Manual Override: only interlocking fields that apply — no punch / geo / time-rule dump.
    if (isPolicyOverrideAttendance(p)) {
      rows.push({
        label: "Attendance marking mode",
        value:
          "Manual — Change Status on attendance rows; working days default Present; WO/PH from Attendance Settings (Weekly Off / Holiday); no check-in/out or punch-based PR/LOP",
      });
      rows.push({
        label: "Check-in / punch / capture",
        value: "Not applicable (Override Attendance)",
      });
      rows.push({
        label: "Time rules / regularization / OT / groups / leave workflow",
        value:
          "Not configured on this policy — use Attendance Settings (Shift, Leave, Weekly Off, Holiday) and Process Attendance lock",
      });
      pushApplicabilityRows();
      return rows;
    }

    rows.push({ label: "Check-in Mobile", value: p.checkInMobile ? "Yes" : "No" });
    rows.push({ label: "Check-in Web", value: p.checkInWeb ? "Yes" : "No" });
    rows.push({ label: "Check-in Biometric", value: p.checkInBiometric ? "Yes" : "No" });
    rows.push({ label: "Check-in Manual", value: yesNo(p.checkInManual) });
    rows.push({ label: "Track location", value: p.trackLocation ? "Yes" : "No" });
    rows.push({
      label: "Restrict location for online check-in",
      value: p.restrictLocationForOnlineCheckIn ? "Yes" : "No",
    });
    rows.push({ label: "Geo-fencing enabled", value: yesNo(p.enableGeoFencing) });
    rows.push({ label: "Geo-fence radius (meters)", value: p.geoFenceRadiusMeters ?? "—" });
    rows.push({ label: "IP restriction", value: yesNo(p.enforceIpRestriction) });
    rows.push({
      label: "Allowed IP ranges",
      value: (p.allowedIpRanges || "").trim() || "—",
    });
    rows.push({ label: "Device binding", value: yesNo(p.enforceDeviceBinding) });
    rows.push({
      label: "Max bound devices / employee",
      value: p.maxBoundDevicesPerEmployee ?? "—",
    });
    rows.push({
      label: "Duplicate punch control",
      value: p.duplicatePunchControlEnabled === false ? "No" : "Yes",
    });
    rows.push({
      label: "Duplicate punch window (minutes)",
      value: p.duplicatePunchWindowMinutes ?? "—",
    });
    rows.push({
      label: "Punch mode",
      value:
        p.punchMode === "single"
          ? "Single punch"
          : p.punchMode === "multiple"
            ? "Multiple punches"
            : "Double punch",
    });
    rows.push({
      label: "Check-in/out rule",
      value:
        p.checkInOutRule === "strict_pairs"
          ? "Strict IN-OUT pairing"
          : p.checkInOutRule === "closest_to_shift"
            ? "Closest to shift window"
            : "First IN / Last OUT",
    });
    rows.push({ label: "Auto logout enabled", value: yesNo(p.autoLogoutEnabled) });
    rows.push({
      label: "Auto logout trigger",
      value:
        p.autoLogoutMode === "total_hours"
          ? `After ${p.autoLogoutAfterHours ?? 0} hour(s)`
          : p.autoLogoutMode === "specific_time"
            ? `At ${p.autoLogoutTime || "23:59"}`
            : "At shift end time",
    });
    rows.push({
      label: "Missing punch handling",
      value:
        p.missingPunchHandling === "auto_mark_default"
          ? `Auto mark (${p.defaultMarkingForMissingPunch || "half_day"})`
          : p.missingPunchHandling === "ignore_for_hours"
            ? "Ignore for hours"
            : "Regularization required",
    });
    rows.push({
      label: "Sandwich rule",
      value: p.sandwichRuleEnabled
        ? `On (WO: ${p.sandwichRuleWeeklyOff !== false ? "Yes" : "No"}, Holiday: ${p.sandwichRuleHoliday !== false ? "Yes" : "No"})`
        : "Off",
    });
    if (p.sandwichRuleEnabled && (p.sandwichRuleNotes || "").trim()) {
      rows.push({ label: "Sandwich rule notes", value: p.sandwichRuleNotes });
    }
    rows.push({
      label: "OT / Compensatory off",
      value:
        p.otCompOffMode === "ot_only"
          ? `OT (min ${p.otMinHoursPerDay ?? 1}h/day, ×${p.otPayMultiplier ?? 1.5})`
          : p.otCompOffMode === "comp_off_only"
            ? `Comp off (min ${p.compOffMinHours ?? 4}h; expiry ${p.compOffExpiryDays ?? 90}d)`
            : p.otCompOffMode === "both"
              ? `OT + Comp off`
              : "None",
    });
    rows.push({ label: "Overtime enabled", value: yesNo(p.overtimeEnabled) });
    rows.push({ label: "OT pre-approval", value: yesNo(p.overtimeRequiresPreApproval) });
    rows.push({ label: "OT minimum threshold (minutes)", value: p.overtimeMinThresholdMinutes ?? "—" });
    rows.push({
      label: "OT rate multiplier",
      value: p.overtimeRateMultiplier ?? p.otPayMultiplier ?? "—",
    });
    rows.push({ label: "OT daily cap (hours)", value: p.overtimeDailyCapHours ?? "—" });
    rows.push({ label: "OT monthly cap (hours)", value: p.overtimeMonthlyCapHours ?? "—" });
    if (p.compensatoryOffEnabled || (p.otCompOffMode && p.otCompOffMode !== "none")) {
      rows.push({
        label: "Comp off conditions",
        value: (p.compensatoryGeneralConditions || "").trim() || "—",
      });
      rows.push({
        label: "Comp off eligibility",
        value: [
          p.compOffEligiblePresent && "Extra hours on present days",
          p.compOffEligibleWeekOffWorked && "Worked on weekly off",
          p.compOffEligibleHolidayWorked && "Worked on holiday",
        ]
          .filter(Boolean)
          .join("; ") || "—",
      });
      rows.push({
        label: "Comp off approval required",
        value: p.compOffRequiresApproval ? "Yes" : "No",
      });
      rows.push({ label: "Comp off accrual unit (hours)", value: p.compOffAccrualUnitHours ?? "—" });
      rows.push({
        label: "Comp off max accrual (days/month)",
        value: p.compOffMaxAccrualDaysPerMonth ?? "—",
      });
      rows.push({ label: "Comp off encashment enabled", value: yesNo(p.compOffEncashmentEnabled) });
      rows.push({ label: "Comp off encashment rate", value: p.compOffEncashmentRate ?? "—" });
    }
    rows.push({ label: "Regularization allowed", value: p.regularizationAllowed ? "Yes" : "No" });
    rows.push({ label: "Change Shift (ESS)", value: p.changeShiftEnabled !== false ? "Yes" : "No" });
    rows.push({ label: "Short Leave (ESS)", value: p.shortLeaveEnabled ? "Yes" : "No" });
    if (p.shortLeaveEnabled) {
      rows.push({ label: "Short leave max (min)", value: p.shortLeaveMaxMinutes ?? 60 });
    }
    rows.push({
      label: "Regularization — days after attendance date",
      value: p.regularizationTillDays ?? "—",
    });
    rows.push({
      label: "Regularization — max locked payroll months back",
      value: p.regularizationLockedMonthsMax != null ? String(p.regularizationLockedMonthsMax) : "—",
    });
    rows.push({
      label: "Regularization — max calendar days back",
      value: p.regularizationMaxCalendarDaysBack != null ? String(p.regularizationMaxCalendarDaysBack) : "—",
    });
    rows.push({
      label: "Regularization — max punch corrections / month",
      value: p.regularizationMaxPunchCorrectionsPerMonth ?? "—",
    });
    rows.push({
      label: "Regularization — max request instances / month",
      value: p.regularizationMaxRequestInstancesPerMonth ?? "—",
    });
    rows.push({
      label: "Regularization cut-off day (month)",
      value: p.regularizationCutOffDayOfMonth ?? "—",
    });
    rows.push({
      label: "Regularization approval levels",
      value: p.regularizationApprovalLevels ?? "—",
    });
    rows.push({
      label: "Regularization reason mandatory",
      value: yesNo(p.regularizationRequireReason !== false),
    });
    rows.push({
      label: "Leave-attendance priority",
      value:
        p.leaveAttendancePriorityRule === "attendance_first"
          ? "Attendance first"
          : p.leaveAttendancePriorityRule === "hybrid"
            ? "Hybrid"
            : "Approved leave first",
    });
    rows.push({
      label: "Half-day leave mapping",
      value: yesNo(p.halfDayLeaveMappingEnabled !== false),
    });
    rows.push({
      label: "Auto absent marking",
      value: yesNo(p.autoAbsentMarkingEnabled !== false),
    });
    rows.push({
      label: "Auto half-day marking",
      value: yesNo(p.autoHalfDayMarkingEnabled !== false),
    });
    rows.push({
      label: "Absent if hours below",
      value: p.absentMarkIfHoursBelow ?? "—",
    });
    rows.push({
      label: "Half-day if hours below",
      value: p.halfDayMarkIfHoursBelow ?? "—",
    });
    rows.push({
      label: "Exclude OD/WFH from auto marking",
      value: `${yesNo(p.excludeOnDutyFromAutoMarking !== false)} / ${yesNo(
        p.excludeWfhFromAutoMarking !== false
      )}`,
    });
    rows.push({ label: "OD request enabled", value: yesNo(p.odRequestEnabled !== false) });
    rows.push({ label: "WFH request enabled", value: yesNo(p.wfhRequestEnabled !== false) });
    rows.push({ label: "OD/WFH approval required", value: yesNo(p.odWfhRequireApproval !== false) });
    rows.push({ label: "OD/WFH tracking mode", value: p.odWfhTrackingMode || "hours" });
    rows.push({
      label: "Attendance freeze (days before month-end)",
      value: p.attendanceFreezeDaysBeforeMonthEnd != null ? String(p.attendanceFreezeDaysBeforeMonthEnd) : "0",
    });
    rows.push({
      label: "Missed punch reminder after (hours)",
      value: p.missedPunchReminderAfterHours ?? "—",
    });
    rows.push({ label: "Penalty engine enabled", value: yesNo(p.penaltyEngineEnabled) });
    rows.push({ label: "Penalty reset cycle", value: p.penaltyResetCycle || "monthly" });
    rows.push({ label: "Penalty rule notes", value: (p.penaltyRuleNotes || "").trim() || "—" });
    rows.push({
      label: "Cut off",
      value:
        p.cutOffMethod === "specific"
          ? `From day ${p.cutOffFromDay} to ${p.cutOffToDay} (next month)`
          : "Calendar month",
    });
    rows.push({ label: "Payroll integration enabled", value: yesNo(p.payrollIntegrationEnabled !== false) });
    rows.push({
      label: "Payroll outputs",
      value: [
        p.payrollExportIncludePresentDays !== false && "Present days",
        p.payrollExportIncludePaidDays !== false && "Paid days",
        p.payrollExportIncludeOtHours !== false && "OT hours",
        p.payrollExportIncludeLopDays !== false && "LOP days",
      ]
        .filter(Boolean)
        .join(", ") || "None",
    });
    rows.push({ label: "Payroll lock enabled", value: yesNo(p.payrollLockEnabled !== false) });
    rows.push({ label: "Payroll lock day", value: p.payrollLockDayOfMonth ?? "—" });
    rows.push({ label: "Notifications enabled", value: yesNo(p.notificationsEnabled !== false) });
    rows.push({
      label: "Escalation",
      value: p.escalationEnabled ? `Yes (after ${p.escalationAfterHours ?? 24}h)` : "No",
    });
    rows.push({
      label: "Reports",
      value: [
        p.dashboardMusterRollEnabled !== false && "Muster roll",
        p.dashboardExceptionReportEnabled !== false && "Exception report",
        p.dashboardAttendanceLogEnabled !== false && "Attendance logs",
      ]
        .filter(Boolean)
        .join(", ") || "None",
    });
    rows.push({
      label: "Compliance caps",
      value: `Daily ${p.complianceWorkingHoursCapPerDay ?? 12}h / Weekly ${
        p.complianceWeeklyHoursCap ?? 60
      }h`,
    });
    rows.push({
      label: "Compliance OT consent",
      value: yesNo(p.complianceOvertimeRequiresConsent !== false),
    });
    rows.push({ label: "Attendance lock enabled", value: yesNo(p.attendanceLockEnabled !== false) });
    rows.push({ label: "Attendance lock after (days)", value: p.attendanceLockAfterDays ?? "—" });
    rows.push({ label: "Audit trail enabled", value: yesNo(p.auditTrailEnabled !== false) });
    rows.push({ label: "Bulk upload enabled", value: yesNo(p.bulkUploadEnabled !== false) });
    rows.push({ label: "API integration enabled", value: yesNo(p.apiIntegrationEnabled) });
    rows.push({ label: "API integration notes", value: (p.apiIntegrationNotes || "").trim() || "—" });
    rows.push({
      label: "Shift",
      value: shifts.find((s) => String(s.id) === String(p.shiftGroupId))?.name || "—",
    });
    rows.push({
      label: "Leave / Holiday / Weekly off",
      value: [
        leaveGroups.find((g) => String(g.id) === String(p.leaveGroupId))?.name,
        holidayGroups.find((g) => String(g.id) === String(p.holidayGroupId))?.name,
        weeklyOffGroups.find((g) => String(g.id) === String(p.weeklyOffGroupId))?.name,
      ]
        .filter(Boolean)
        .join(" / ") || "—",
    });
    {
      const aw = p.activityWorkflows || {};
      const keys = Object.keys(aw);
      if (keys.length) {
        rows.push({
          label: "Activity workflows",
          value: keys
            .map((k) => {
              const m = aw[k]?.approvalMethod;
              const method =
                m === "workflow"
                  ? "workflow"
                  : m === "on_instance"
                    ? "on instance"
                    : "auto";
              return `${k}: ${method}`;
            })
            .join("; "),
        });
      }
    }
    pushApplicabilityRows();
    return rows;
  };

  const getDynamicPolicyGuideLines = (policy) => {
    const p = policy || createDefaultPolicy();
    if (isPolicyOverrideAttendance(p)) {
      return [
        `Override Attendance: employees on this policy use Manual marking (Change Status), not punches.`,
        `Working days default to Present; Weekly Off and Public Holiday come from Attendance Settings tabs (not policy Groups).`,
        `Check-in channels, geo/IP/device capture, punch mode, time rules, regularization, OT, and leave workflow are not part of this policy path.`,
        `Month lock uses Process Attendance (existing lock), not policy freeze / cut-off on this short wizard.`,
        `Wizard path: Basics → Check-in (Auto + Override) → Apply (applicability).`,
      ];
    }
    return [
      `Check-in channels: mobile / web / biometric; optional location tracking and geo-fencing.`,
      `Capture validation: optional IP restriction, device binding, duplicate punch window, and manual-entry controls.`,
      `Punch logic: configure single/double/multi punch behavior, first-IN/last-OUT or strict pairing, auto-logout, and missing-punch handling.`,
      `Hours engine: choose hours calc method, fixed or punch-based break deduction, and rounding rules for payroll-friendly totals.`,
      `Sandwich rule: when enabled, weekly off or holidays falling between two leave days may be counted as leave (configure WO / holiday separately).`,
      `Shifts can carry their own grace, work-hour thresholds, late/early penalties, and optional shift-override approval workflow (Create shift form).`,
      `Shift master supports fixed/flexi setup, bandwidth + core hours, cross-day flag, buffers, and rotation controls.`,
      `OT vs compensatory off: choose none, OT only, comp off only, or both; set minimum hours, multipliers, eligibility (present / WO worked / holiday worked), and expiry.`,
      `Leave-attendance integration: apply priority rules, half-day mapping, auto absent/half-day thresholds, and OD/WFH exceptions.`,
      `Regularization: combine “days after date”, locked payroll months, calendar lookback, and caps on punch corrections or request instances per month.`,
      `Policy controls cover payroll outputs + lock, notifications/escalation, compliance caps, audit trail, bulk upload, and API integration.`,
      `Link shift, leave, holiday, and weekly-off groups; use + to create groups. Freeze days before month-end and missed-punch reminders reduce payroll disputes.`,
      `Leave approval: ${p.approvalMethod === "workflow" ? "multi-step workflow" : p.approvalMethod === "on_instance" ? "on instance" : "auto approved"}.`,
    ];
  };

  const escapeXml = (s) => {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  const buildAttendanceProcessSvg = (policy) => {
    const p = policy || {};
    const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
    const cx = 460;
    let y = 28;
    const parts = [];
    const pushLine = (y0, y1) => {
      parts.push(
        `<line x1="${cx}" y1="${y0}" x2="${cx}" y2="${y1}" stroke="#64748b" stroke-width="2" marker-end="url(#ap-flow-arr)"/>`
      );
    };
    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="14" font-weight="700" fill="#0f172a">Attendance &amp; leave flow</text>`
    );
    y += 24;
    parts.push(
      `<text x="${cx}" y="${y}" text-anchor="middle" font-size="9" fill="#64748b">Check-in → hours / late rules → regularization → leave approval → payroll cut-off</text>`
    );
    y += 28;
    parts.push(`<circle cx="${cx}" cy="${y}" r="12" stroke="#64748b" fill="#fff" stroke-width="2"/>`);
    parts.push(`<text x="${cx}" y="${y + 4}" text-anchor="middle" font-size="8" fill="#334155">START</text>`);
    y += 20;
    pushLine(y - 6, y);
    parts.push(
      `<rect x="${cx - 118}" y="${y}" width="236" height="36" rx="6" fill="#f8fafc" stroke="#334155" stroke-width="1.2"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 22}" text-anchor="middle" font-size="10" font-weight="600" fill="#0f172a">Check-in (configured channels)</text>`
    );
    y += 48;
    pushLine(y - 12, y);
    parts.push(
      `<rect x="${cx - 118}" y="${y}" width="236" height="34" rx="6" fill="#fff" stroke="#64748b" stroke-width="1"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 21}" text-anchor="middle" font-size="9" fill="#475569">Work hours · grace · late / early · half / full day</text>`
    );
    y += 46;
    pushLine(y - 12, y);
    parts.push(
      `<rect x="${cx - 118}" y="${y}" width="236" height="32" rx="6" fill="#fefce8" stroke="#ca8a04" stroke-width="1"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 20}" text-anchor="middle" font-size="9" fill="#713f12">Regularization (if allowed, within window)</text>`
    );
    y += 44;
    if (wf.length) {
      wf.forEach((s, i) => {
        pushLine(y - 12, y);
        const role = getRoleLabel(WORKFLOW_ROLES, s.role);
        const sub = s.notifyOnly ? "Notify only" : `SLA ${s.slaDays || 3}d`;
        parts.push(
          `<rect x="${cx - 112}" y="${y}" width="224" height="36" rx="6" fill="#f0fdf4" stroke="#16a34a" stroke-width="1.1"/>`
        );
        parts.push(
          `<text x="${cx}" y="${y + 15}" text-anchor="middle" font-size="9" font-weight="600" fill="#0f172a">Leave / request step ${i + 1}: ${escapeXml(role)}</text>`
        );
        parts.push(
          `<text x="${cx}" y="${y + 30}" text-anchor="middle" font-size="8" fill="#475569">${escapeXml(sub)}</text>`
        );
        y += 48;
      });
    } else {
      pushLine(y - 12, y);
      parts.push(
        `<rect x="${cx - 112}" y="${y}" width="224" height="32" rx="6" fill="#f8fafc" stroke="#94a3b8" stroke-width="1"/>`
      );
      parts.push(
        `<text x="${cx}" y="${y + 20}" text-anchor="middle" font-size="8.5" fill="#64748b">No workflow steps — follows approval method</text>`
      );
      y += 44;
    }
    pushLine(y - 12, y);
    parts.push(
      `<rect x="${cx - 118}" y="${y}" width="236" height="30" rx="6" fill="#eef2ff" stroke="#4f46e5" stroke-width="1"/>`
    );
    parts.push(
      `<text x="${cx}" y="${y + 19}" text-anchor="middle" font-size="9" fill="#312e81">Attendance cut-off → payroll period</text>`
    );
    y += 42;
    parts.push(`<circle cx="${cx}" cy="${y}" r="11" stroke="#0f172a" fill="#fff" stroke-width="2"/>`);
    parts.push(`<text x="${cx}" y="${y + 4}" text-anchor="middle" font-size="8" fill="#334155">END</text>`);
    const H = y + 24;
    const W = 920;
    return `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="max-width:100%;height:auto"><defs><marker id="ap-flow-arr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><polygon points="0 0, 8 4, 0 8" fill="#64748b"/></marker></defs>${parts.join("")}</svg>`;
  };

  const printCombinedFlowchartPdf = (policy) => {
    const html = `<!DOCTYPE html><html><head><title>Attendance flow</title><style>body{font-family:"Segoe UI",Inter,Arial,sans-serif;margin:20px;}h2{font-size:15px;}</style></head><body><h2>${escapeXml(
      policy?.policyName || "Policy"
    )}</h2><div>${buildAttendanceProcessSvg(policy || {})}</div></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const printPolicyDocument = (policy) => {
    printSinglePolicyAsDocument(
      "Attendance Policy",
      policy.policyName,
      policy.effectiveDate,
      getPolicySummaryRows,
      policy
    );
  };

  const printPolicyFullDocument = (policy) => {
    const html = buildAttendancePolicyDocumentHtml(policy, policyDocumentOptions);
    printAttendancePolicyDocumentHtml(html);
  };

  const printPolicySnapshotDocument = (snapshotPolicy) => {
    const html = buildAttendancePolicyDocumentHtml(snapshotPolicy, policyDocumentOptions);
    printAttendancePolicyDocumentHtml(html);
  };

  const openCreateForm = () => {
    setIsEditing(false);
    setEditingPolicyId(null);
    setPolicySaveError(null);
    setSaveGateModal(null);
    setPolicyHelpOpen(false);
    setNewPolicy(createDefaultPolicy());
    setIsFormOpen(true);
  };

  const openEditForm = (policy) => {
    setIsEditing(true);
    setEditingPolicyId(policy.id);
    setPolicySaveError(null);
    setSaveGateModal(null);
    setPolicyHelpOpen(false);
    setNewPolicy({
      ...createDefaultPolicy(),
      ...policy,
      autoAttendance: !!(
        policy.autoAttendance || policy.processMode === "manual"
      ),
      overrideAttendance: !!(
        policy.overrideAttendance || policy.processMode === "manual"
      ),
      policyName: policy.policyName || policy.name || "",
      workflowSteps: Array.isArray(policy.workflowSteps)
        ? policy.workflowSteps.map(normalizeWorkflowStep)
        : [],
      conditionalWorkflowRules: Array.isArray(policy.conditionalWorkflowRules)
        ? policy.conditionalWorkflowRules.map((r) => ({
            ...r,
            workflowSteps: (r.workflowSteps || []).map(normalizeWorkflowStep),
          }))
        : [],
      allowedLocations: Array.isArray(policy.allowedLocations) ? policy.allowedLocations : [],
      employmentTypes: Array.isArray(policy.employmentTypes) ? policy.employmentTypes : [],
    });
    setPolicyFormStep(1);
    setIsFormOpen(true);
  };

  const validateAttendancePolicyStep = (step) => {
    if (step === 1 && !String(newPolicy.policyName || "").trim()) {
      setPolicySaveError("Policy name is required.");
      return false;
    }
    const overrideMode = isAttendancePolicyOverrideMode(newPolicy);
    if (step === 3 && !overrideMode && !newPolicy.shiftGroupId) {
      setPolicySaveError("Select a default shift (create one under Shift tab if needed).");
      return false;
    }
    setPolicySaveError(null);
    return true;
  };

  const goAttendancePolicyNext = () => {
    if (!validateAttendancePolicyStep(policyFormStep)) return;
    const next = getNextAttendancePolicyFormStep(newPolicy, policyFormStep);
    if (next != null) {
      setPolicyFormStep(next);
      return;
    }
    requestSavePolicyForm();
  };

  const goAttendancePolicyPrev = () => {
    setPolicyFormStep(getPrevAttendancePolicyFormStep(newPolicy, policyFormStep));
  };

  const requestSavePolicyForm = (opts = {}) => {
    const skipOverlapCheck = !!opts.skipOverlapCheck;
    setPolicySaveError(null);
    setSaveGateModal(null);
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const candidate = {
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    };

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(saved);
        const sigNew = policyContentSignature({ ...candidate, id: saved.id });
        // Soft note only — do not block mid-month edits when attendance/payroll are still open.
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (candidate.effectiveDate || "")) {
          toast(
            "Policy updated with the same effective date. Re-run the attendance schedule so day marks refresh.",
            { icon: "ℹ️", duration: 4500 }
          );
        }
      }
    }

    if (!skipOverlapCheck) {
      const conflicts = findConflictingPolicies(
        { ...candidate, id: candidate.id || editingPolicyId || "__new__" },
        policies,
        isEditing ? editingPolicyId : null
      );
      if (conflicts.length > 0) {
        setSaveGateModal({
          type: "overlap",
          items: conflicts.map((p) => ({
            id: p.id,
            name: p.policyName || p.name || "Untitled policy",
            detail: summarizeOverlapReason(candidate, p),
          })),
        });
        return;
      }
    }

    if (!String(newPolicy.policyName || "").trim()) {
      setPolicySaveError("Policy name is required.");
      return;
    }
    if (!isAttendancePolicyOverrideMode(candidate)) {
      const activityWfs = newPolicy.activityWorkflows || {};
      const badActivity = Object.entries(activityWfs).find(([, wf]) => {
        if (!wf || wf.approvalMethod !== "workflow") return false;
        return !Array.isArray(wf.workflowSteps) || wf.workflowSteps.length === 0;
      });
      if (badActivity) {
        setPolicySaveError(
          `Activity workflow "${badActivity[0]}" is set to Through workflow but has no steps.`
        );
        return;
      }
    }
    setShowSummaryModal(true);
  };

  const continueAfterOverlapAck = () => {
    setSaveGateModal(null);
    requestSavePolicyForm({ skipOverlapCheck: true });
  };

  const handleSavePolicy = async () => {
    const normalizedIndividualIds = String(newPolicy.individualEmployeeIds || "")
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", ");
    const payload = normalizePolicyRecord({
      ...newPolicy,
      individualEmployeeIds: normalizedIndividualIds,
      employmentTypes: Array.isArray(newPolicy.employmentTypes) ? newPolicy.employmentTypes : [],
    });

    if (isEditing && editingPolicyId) {
      const saved = policies.find((p) => p.id === editingPolicyId);
      if (saved) {
        const sigOld = policyContentSignature(saved);
        const sigNew = policyContentSignature({ ...payload, id: saved.id });
        if (sigOld !== sigNew && (saved.effectiveDate || "") === (payload.effectiveDate || "")) {
          // Allowed: open months can keep the same effective date. Version history still increments.
        }
      }
    }

    try {
      if (isEditing && editingPolicyId) {
        const p0 = policies.find((p) => p.id === editingPolicyId);
        if (!p0) {
          toast.error("Policy not found");
          return;
        }
        const computedV = computeVersionOnSave(p0, payload, false);
        const savedAt = new Date().toISOString();
        const merged = {
          ...p0,
          ...payload,
          policyVersion: computedV.policyVersion,
          policyVersionHistory: computedV.policyVersionHistory,
          updatedAt: savedAt,
          approvalRolesForPolicy,
          policyDocumentPushed: p0.policyDocumentPushed,
          policyPushedAt: p0.policyPushedAt,
        };
        const policySnapshots = mergePolicySnapshots(p0, merged, computedV, false, savedAt);
        const body = { ...merged, policySnapshots };
        const isServerId = /^\d+$/.test(String(editingPolicyId));
        let savedRow = body;
        if (isServerId) {
          const res = await attendanceStructureService.updateAttendancePolicy(
            editingPolicyId,
            body
          );
          savedRow = normalizePolicyRecord(res?.data || res || body);
        } else {
          const res = await attendanceStructureService.createAttendancePolicy(body);
          savedRow = normalizePolicyRecord(res?.data || res || body);
        }
        setPolicies((prev) =>
          prev.map((p) => (p.id === editingPolicyId ? { ...savedRow, id: String(savedRow.id) } : p))
        );
        toast.success(
          computedV.versionBumped
            ? "Policy saved. Document version increased."
            : "Attendance policy updated"
        );
      } else {
        const savedAt = new Date().toISOString();
        const v = computeVersionOnSave(null, payload, true);
        const merged = {
          ...payload,
          policyVersion: v.policyVersion,
          policyVersionHistory: v.policyVersionHistory,
          status: "active",
          approvalRolesForPolicy,
          createdAt: savedAt,
          updatedAt: savedAt,
          policyDocumentPushed: false,
          policyPushedAt: null,
        };
        merged.policySnapshots = mergePolicySnapshots(null, merged, v, true, savedAt);
        const res = await attendanceStructureService.createAttendancePolicy(merged);
        const savedRow = normalizePolicyRecord(res?.data || res || merged);
        setPolicies((prev) => [{ ...savedRow, id: String(savedRow.id) }, ...prev]);
        toast.success("Attendance policy created");
      }

      setIsFormOpen(false);
      setIsEditing(false);
      setEditingPolicyId(null);
      setShowSummaryModal(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Failed to save attendance policy");
    }
  };

  const handleCancelPolicy = () => {
    setIsFormOpen(false);
    setIsEditing(false);
    setEditingPolicyId(null);
    setPolicyHelpOpen(false);
    setPolicySaveError(null);
    setSaveGateModal(null);
  };

  const handleDeactivatePolicy = async (policyId) => {
    try {
      if (/^\d+$/.test(String(policyId))) {
        const res = await attendanceStructureService.updateAttendancePolicy(policyId, {
          status: "inactive",
          isActive: false,
        });
        const row = normalizePolicyRecord(res?.data || { id: policyId, status: "inactive" });
        setPolicies((prev) =>
          prev.map((p) => (p.id === policyId ? { ...p, ...row, status: "inactive" } : p))
        );
      } else {
        setPolicies((prev) =>
          prev.map((p) => (p.id === policyId ? { ...p, status: "inactive" } : p))
        );
      }
      toast.success("Policy deactivated");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to deactivate");
    }
  };

  const handleReactivatePolicy = async (policyId) => {
    try {
      if (/^\d+$/.test(String(policyId))) {
        const res = await attendanceStructureService.updateAttendancePolicy(policyId, {
          status: "active",
          isActive: true,
        });
        const row = normalizePolicyRecord(res?.data || { id: policyId, status: "active" });
        setPolicies((prev) =>
          prev.map((p) => (p.id === policyId ? { ...p, ...row, status: "active" } : p))
        );
      } else {
        setPolicies((prev) =>
          prev.map((p) => (p.id === policyId ? { ...p, status: "active" } : p))
        );
      }
      toast.success("Policy reactivated");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to reactivate");
    }
  };

  const handleDeletePolicy = async (policyId) => {
    const policy = policies.find((p) => String(p.id) === String(policyId));
    if (!policy) return;
    // Assigned to employees → deactivate only (cannot hard-delete).
    const assigned =
      (Array.isArray(policy.individualEmployeeIds) &&
        policy.individualEmployeeIds.length > 0) ||
      !!policy.allEmployees ||
      ["branch", "department", "designation", "grade", "category", "subDepartment", "location"].some(
        (k) => policy[k]
      ) ||
      (Array.isArray(policy.employmentTypes) && policy.employmentTypes.length > 0);
    try {
      if (/^\d+$/.test(String(policyId))) {
        if (assigned) {
          const res = await attendanceStructureService.deleteAttendancePolicy(policyId, {
            soft: true,
          });
          const row = normalizePolicyRecord(res?.data || { id: policyId, status: "inactive" });
          setPolicies((prev) =>
            prev.map((p) => (p.id === policyId ? { ...p, ...row, status: "inactive" } : p))
          );
          toast.success("Policy is assigned — deactivated instead of deleted");
          return;
        }
        await attendanceStructureService.deleteAttendancePolicy(policyId);
        setPolicies((prev) => prev.filter((p) => p.id !== policyId));
        toast.success("Policy deleted");
        return;
      }
      if (assigned) {
        setPolicies((prev) =>
          prev.map((p) => (p.id === policyId ? { ...p, status: "inactive" } : p))
        );
        toast.success("Policy is assigned — deactivated instead of deleted");
        return;
      }
      setPolicies((prev) => prev.filter((p) => p.id !== policyId));
      toast.success("Policy deleted");
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to delete policy");
    }
  };

  const handleCopyPolicy = (policy) => {
    const now = new Date().toISOString();
    const nm = `${policy.policyName || policy.name || "Policy"} (Copy)`;
    const eff = policy.effectiveDate || new Date().toISOString().slice(0, 10);
    const copiedPolicy = {
      ...createDefaultPolicy(),
      ...policy,
      id: Date.now().toString(),
      policyName: nm,
      status: "active",
      createdAt: now,
      updatedAt: now,
      policyDocumentPushed: false,
      policyPushedAt: null,
      policyVersion: 1,
      policyVersionHistory: [
        { version: 1, effectiveDate: eff, savedAt: now, event: "created", policyName: nm },
      ],
    };
    const snapBody = clonePolicySnapshot({ ...copiedPolicy, policySnapshots: undefined, policyVersion: 1 });
    copiedPolicy.policySnapshots = [{ version: 1, effectiveDate: eff, savedAt: now, policy: snapBody }];
    setPolicies((prev) => [copiedPolicy, ...prev]);
    toast.success("Policy copied");
  };

  const handlePushPolicyDocument = (policy) => {
    const now = new Date().toISOString();
    setPolicies((prev) =>
      prev.map((p) =>
        p.id === policy.id ? { ...p, policyDocumentPushed: true, policyPushedAt: now, updatedAt: now } : p
      )
    );
    setPolicyDocModal((m) =>
      m && m.id === policy.id ? { ...m, policyDocumentPushed: true, policyPushedAt: now } : m
    );
    setActionMenu(null);
    toast.success("Policy document published.");
  };

  const openPolicyDocumentPreview = (policy) => {
    const latest = policies.find((p) => p.id === policy.id) || policy;
    setPolicyDocModal(latest);
    setActionMenu(null);
  };

  const openActionMenuFromEvent = (e, policy) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const menuApproxHeight = 320;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow < menuApproxHeight && rect.top > menuApproxHeight
        ? Math.max(8, rect.top - menuApproxHeight - 4)
        : rect.bottom + 4;
    setActionMenu((prev) => (prev?.policy?.id === policy.id ? null : { policy, top, left }));
  };

  const openShiftActionMenuFromEvent = (e, shift) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 144;
    const menuApproxHeight = 132;
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow < menuApproxHeight && rect.top > menuApproxHeight
        ? Math.max(8, rect.top - menuApproxHeight - 4)
        : rect.bottom + 4;
    setShiftActionMenu((prev) =>
      String(prev?.shift?.id) === String(shift.id) ? null : { shift, top, left }
    );
  };

  const addWorkflowStep = () => {
    setNewPolicy((prev) => ({
      ...prev,
      workflowSteps: [
        ...(prev.workflowSteps || []),
        { role: defaultWorkflowRole, slaDays: 3, notifyOnly: false },
      ],
    }));
  };
  const removeWorkflowStep = (index) => {
    setNewPolicy((prev) => ({
      ...prev,
      workflowSteps: (prev.workflowSteps || []).filter((_, i) => i !== index),
    }));
  };
  const updateWorkflowStep = (index, field, value) => {
    setNewPolicy((prev) => ({
      ...prev,
      workflowSteps: (prev.workflowSteps || []).map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      ),
    }));
  };

  const toggleApprovalRole = (roleValue) => {
    setApprovalRolesForPolicy((prev) =>
      prev.includes(roleValue) ? prev.filter((r) => r !== roleValue) : [...prev, roleValue]
    );
  };

  const toggleEmploymentType = (value) => {
    setNewPolicy((prev) => {
      const cur = Array.isArray(prev.employmentTypes) ? prev.employmentTypes : [];
      const has = cur.includes(value);
      return { ...prev, employmentTypes: has ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };

  const addAllowedLocation = () =>
    setNewPolicy((f) => ({
      ...f,
      allowedLocations: [
        ...(f.allowedLocations || []),
        { id: "loc_" + Date.now(), name: "", address: "", radiusMeters: 100 },
      ],
    }));
  const updateAllowedLocation = (idx, field, value) =>
    setNewPolicy((f) => ({
      ...f,
      allowedLocations: (f.allowedLocations || []).map((loc, i) =>
        i === idx ? { ...loc, [field]: value } : loc
      ),
    }));
  const removeAllowedLocation = (idx) =>
    setNewPolicy((f) => ({
      ...f,
      allowedLocations: (f.allowedLocations || []).filter((_, i) => i !== idx),
    }));

  const addConditionalRule = () =>
    setNewPolicy((f) => ({
      ...f,
      conditionalWorkflowRules: [
        ...(f.conditionalWorkflowRules || []),
        {
          id: "cr_" + Date.now(),
          conditionType: "leave_type",
          conditionValue: "",
          workflowSteps: [{ role: defaultWorkflowRole, slaDays: 3, notifyOnly: false }],
        },
      ],
    }));
  const removeConditionalRule = (idx) =>
    setNewPolicy((f) => ({
      ...f,
      conditionalWorkflowRules: (f.conditionalWorkflowRules || []).filter((_, i) => i !== idx),
    }));
  const updateConditionalRule = (idx, field, value) =>
    setNewPolicy((f) => ({
      ...f,
      conditionalWorkflowRules: (f.conditionalWorkflowRules || []).map((r, i) =>
        i === idx ? { ...r, [field]: value } : r
      ),
    }));
  const addConditionalRuleStep = (ruleIdx) =>
    setNewPolicy((f) => {
      const rules = f.conditionalWorkflowRules || [];
      return {
        ...f,
        conditionalWorkflowRules: rules.map((r, i) =>
          i === ruleIdx
            ? {
                ...r,
                workflowSteps: [
                  ...(r.workflowSteps || []),
                  { role: defaultWorkflowRole, slaDays: 3, notifyOnly: false },
                ],
              }
            : r
        ),
      };
    });
  const removeConditionalRuleStep = (ruleIdx, stepIdx) =>
    setNewPolicy((f) => ({
      ...f,
      conditionalWorkflowRules: (f.conditionalWorkflowRules || []).map((r, i) =>
        i === ruleIdx
          ? { ...r, workflowSteps: (r.workflowSteps || []).filter((_, j) => j !== stepIdx) }
          : r
      ),
    }));
  const updateConditionalRuleStep = (ruleIdx, stepIdx, field, value) =>
    setNewPolicy((f) => ({
      ...f,
      conditionalWorkflowRules: (f.conditionalWorkflowRules || []).map((r, i) =>
        i === ruleIdx
          ? {
              ...r,
              workflowSteps: (r.workflowSteps || []).map((s, j) =>
                j === stepIdx ? { ...s, [field]: value } : s
              ),
            }
          : r
      ),
    }));

  const addShiftOverrideWorkflowStep = () =>
    setCreateShiftForm((f) => ({
      ...f,
      shiftOverrideWorkflowSteps: [
        ...(f.shiftOverrideWorkflowSteps || []),
        { role: defaultWorkflowRole, slaDays: 3, notifyOnly: false },
      ],
    }));
  const removeShiftOverrideWorkflowStep = (idx) =>
    setCreateShiftForm((f) => ({
      ...f,
      shiftOverrideWorkflowSteps: (f.shiftOverrideWorkflowSteps || []).filter((_, i) => i !== idx),
    }));
  const updateShiftOverrideWorkflowStep = (idx, field, value) =>
    setCreateShiftForm((f) => ({
      ...f,
      shiftOverrideWorkflowSteps: (f.shiftOverrideWorkflowSteps || []).map((s, i) =>
        i === idx ? { ...s, [field]: value } : s
      ),
    }));
  const computeShiftHours = (startTime, endTime) => {
    const parse = (t) => {
      const [h, m] = String(t || "").split(":").map((v) => Number(v));
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const start = parse(startTime);
    const end = parse(endTime);
    if (start == null || end == null) return 0;
    const endAdjusted = end <= start ? end + 24 * 60 : end;
    const mins = Math.max(0, endAdjusted - start);
    return Number((mins / 60).toFixed(2));
  };
  const isFlexiShiftForm = (form) => {
    if (!form) return false;
    if (form.shiftType) return form.shiftType === "flexi";
    return !!form.flexiEnabled;
  };
  const closeShiftModal = () => {
    setCreateModal(null);
    setEditingShiftId(null);
    setCreateShiftForm(emptyCreateShiftForm());
  };

  const persistShiftCatalog = async (list) => {
    const rows = Array.isArray(list) ? list : [];
    for (const s of rows) {
      if (!s || (!s.id && !s.name)) continue;
      try {
        await attendanceStructureService.createAttendanceShift(s);
      } catch (e) {
        console.warn("Failed to persist attendance shift", s?.id || s?.name, e);
      }
    }
  };

  const addShift = () => {
    const id = editingShiftId || "s_" + Date.now();
    const name = createShiftForm.name.trim() || "New Shift";
    const isFlexi = isFlexiShiftForm(createShiftForm);
    const computedHours = isFlexi
      ? Number(createShiftForm.expectedWorkHours) || 0
      : computeShiftHours(createShiftForm.startTime, createShiftForm.endTime);
    const breakHandling = createShiftForm.breakHandling || "deduct_from_work_hours";
    const totalBreakMinutes =
      breakHandling === "none" ? 0 : Math.max(0, Number(createShiftForm.totalBreakMinutes) || 0);
    const paidBreakMinutes =
      breakHandling === "none" ? 0 : Math.max(0, Number(createShiftForm.paidBreakMinutes) || 0);
    const unpaidBreakMinutes =
      breakHandling === "deduct_from_work_hours"
        ? Math.max(0, totalBreakMinutes - Math.min(totalBreakMinutes, paidBreakMinutes))
        : 0;
    const shift = normalizeShift({
      ...createShiftForm,
      id,
      name,
      flexiEnabled: isFlexi,
      expectedWorkHours: computedHours,
      totalBreakMinutes,
      paidBreakMinutes: Math.min(totalBreakMinutes, paidBreakMinutes),
      unpaidBreakMinutes,
      status: createShiftForm.status || "active",
      breakMinutes: unpaidBreakMinutes,
      shiftOverrideWorkflowSteps: (createShiftForm.shiftOverrideWorkflowSteps || []).map(
        normalizeShiftWorkflowStep
      ),
    });
    setShifts((prev) => {
      const base =
        editingShiftId != null
          ? (prev || []).map((s) => (String(s.id) === String(editingShiftId) ? shift : s))
          : [...(prev || []), shift];
      const next =
        shift.isDefault || (base.length > 0 && !base.some((s) => s.isDefault))
          ? base.map((s) => ({ ...s, isDefault: String(s.id) === String(id) }))
          : base;
      void persistShiftCatalog(next);
      return next;
    });
    setNewPolicy((f) => ({ ...f, shiftGroupId: id }));
    closeShiftModal();
  };
  const setShiftAsDefault = (shiftId) => {
    setShifts((prev) => {
      const next = (prev || []).map((s) => ({
        ...s,
        isDefault: String(s.id) === String(shiftId),
      }));
      void persistShiftCatalog(next);
      return next;
    });
  };
  const openEditShift = (shift) => {
    setEditingShiftId(shift.id);
    setCreateShiftForm({
      ...emptyCreateShiftForm(),
      ...normalizeShift(shift),
      shiftOverrideWorkflowSteps: (shift.shiftOverrideWorkflowSteps || []).map(
        normalizeShiftWorkflowStep
      ),
    });
    setCreateModal("shift");
    setShiftActionMenu(null);
  };
  const deactivateShift = (shiftId) => {
    setShifts((prev) => {
      let next = (prev || []).map((s) =>
        String(s.id) === String(shiftId) ? { ...s, status: "inactive", isDefault: false } : s
      );
      if (!next.some((s) => s.isDefault && (s.status || "active") === "active")) {
        const firstActive = next.find((s) => (s.status || "active") === "active");
        if (firstActive) {
          next = next.map((s) => ({ ...s, isDefault: String(s.id) === String(firstActive.id) }));
        }
      }
      void persistShiftCatalog(next);
      return next;
    });
    setShiftActionMenu(null);
  };
  const activateShift = (shiftId) => {
    setShifts((prev) => {
      const next = (prev || []).map((s) =>
        String(s.id) === String(shiftId) ? { ...s, status: "active" } : s
      );
      void persistShiftCatalog(next);
      return next;
    });
    setShiftActionMenu(null);
  };
  const deleteShift = (shiftId) => {
    void attendanceStructureService.deleteAttendanceShift(shiftId).catch((e) => {
      console.warn("Failed to delete attendance shift", shiftId, e);
    });
    setShifts((prev) => {
      let next = (prev || []).filter((s) => String(s.id) !== String(shiftId));
      if (!next.some((s) => s.isDefault && (s.status || "active") === "active")) {
        const firstActive = next.find((s) => (s.status || "active") === "active");
        if (firstActive) {
          next = next.map((s) => ({ ...s, isDefault: String(s.id) === String(firstActive.id) }));
        }
      }
      void persistShiftCatalog(next);
      return next;
    });
    setShiftActionMenu(null);
  };
  const addLeaveGroup = () => {
    const id = "lg_" + Date.now();
    const name = createLeaveForm.name.trim() || "New Leave Group";
    setLeaveGroups((prev) => [...prev, { id, name, description: createLeaveForm.description || "" }]);
    setNewPolicy((f) => ({ ...f, leaveGroupId: id }));
    setCreateModal(null);
    setCreateLeaveForm({ name: "", description: "" });
  };
  const toggleWeeklyOffDay = (day) => {
    setCreateWeeklyOffForm((f) => ({
      ...f,
      offDays: f.offDays.includes(day) ? f.offDays.filter((d) => d !== day) : [...f.offDays, day].sort((a, b) => a - b),
    }));
  };
  const addWeeklyOffGroup = () => {
    const id = "wo_" + Date.now();
    const name = createWeeklyOffForm.name.trim() || "New Weekly Off";
    setWeeklyOffGroups((prev) => [...prev, { id, name, offDays: createWeeklyOffForm.offDays }]);
    setNewPolicy((f) => ({ ...f, weeklyOffGroupId: id }));
    setCreateModal(null);
    setCreateWeeklyOffForm({ name: "", offDays: [] });
  };
  const addHolidayToForm = () =>
    setCreateHolidayForm((f) => ({ ...f, holidays: [...(f.holidays || []), { date: "", name: "" }] }));
  const updateHolidayInForm = (idx, field, value) =>
    setCreateHolidayForm((f) => ({
      ...f,
      holidays: (f.holidays || []).map((h, i) => (i === idx ? { ...h, [field]: value } : h)),
    }));
  const removeHolidayFromForm = (idx) =>
    setCreateHolidayForm((f) => ({
      ...f,
      holidays: (f.holidays || []).filter((_, i) => i !== idx),
    }));
  const addHolidayGroup = () => {
    const id = "hg_" + Date.now();
    const name = createHolidayForm.name.trim() || "New Holiday Group";
    const holidays = (createHolidayForm.holidays || []).filter((h) => h.date && h.name);
    setHolidayGroups((prev) => [...prev, { id, name, holidays }]);
    setNewPolicy((f) => ({ ...f, holidayGroupId: id }));
    setCreateModal(null);
    setCreateHolidayForm({ name: "", holidays: [{ date: "", name: "" }] });
  };

  useEffect(() => {
    if (!policyHelpOpen) return;
    const onMouseDown = (e) => {
      if (policyHelpRef.current && !policyHelpRef.current.contains(e.target)) setPolicyHelpOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [policyHelpOpen]);

  useEffect(() => {
    if (!approvalSettingsOpen) return;
    const onMouseDown = (e) => {
      if (approvalSettingsRef.current && !approvalSettingsRef.current.contains(e.target))
        setApprovalSettingsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [approvalSettingsOpen]);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [actionMenu]);
  useEffect(() => {
    if (!shiftActionMenu) return;
    const close = () => setShiftActionMenu(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [shiftActionMenu]);

  const getApplicabilityBadges = (policy) => {
    const badges = [];
    if (policy.allEmployees) badges.push("All employees");
    if (!policy.allEmployees) {
      if (policy.branch)
        badges.push(`Branch: ${masterName(branchesList, policy.branch) || policy.branch}`);
      if (policy.department)
        badges.push(`Dept: ${masterName(departmentsList, policy.department) || policy.department}`);
      if (policy.designation)
        badges.push(`Desig: ${masterName(designationsList, policy.designation) || policy.designation}`);
    }
    if (Array.isArray(policy.employmentTypes) && policy.employmentTypes.length) {
      const labels = policy.employmentTypes.map(
        (v) => EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v
      );
      badges.push(`Employment: ${labels.join(", ")}`);
    }
    return badges.length ? badges : ["Scope: see form"];
  };

  const visiblePolicies = policies
    .filter((p) => (showInactivePolicies ? p.status === "inactive" : p.status === "active"))
    .filter((p) =>
      String(p.policyName || p.name || "")
        .toLowerCase()
        .includes(String(policySearch || "").toLowerCase().trim())
    );
  const listedPolicies = visiblePolicies.slice(0, 10);
  const visibleShifts = (shifts || []).filter((s) =>
    showInactiveShifts ? (s.status || "active") === "inactive" : (s.status || "active") === "active"
  );

  const selectedApprovalRoleLabels = useMemo(
    () => APPROVAL_ROLES.filter((r) => approvalRolesForPolicy.includes(r.value)).map((r) => r.label),
    [approvalRolesForPolicy]
  );

  const inputCls = darkMode
    ? "bg-gray-800 border-gray-500 text-white rounded-lg"
    : "bg-white border-gray-400 text-gray-900 rounded-lg";
  const sectionCls = darkMode
    ? "border-gray-600 bg-gray-700/80 rounded-xl"
    : "border-gray-300 bg-white rounded-xl";
  const shiftOnlyMode = mode === "shift";
  const currentShiftIsFlexi = isFlexiShiftForm(createShiftForm);

  return (
    <div
      className={`p-4 rounded-none border ${
        darkMode
          ? "bg-gray-700 border-gray-600 text-gray-100"
          : "bg-white border-gray-300 text-slate-900"
      } h-full overflow-y-auto`}
    >
      {shiftOnlyMode ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className={`text-base font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}>
              Shift management
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowInactiveShifts((v) => !v)}
                className={`px-3 py-1.5 text-sm rounded-none ${
                  darkMode ? "bg-gray-600 text-white" : "bg-gray-100 text-gray-700 border"
                }`}
              >
                {showInactiveShifts ? "Show active" : "Show inactive"}
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rh-btn-primary hover:opacity-95 rounded-none"
                onClick={() => {
                  setEditingShiftId(null);
                  setCreateShiftForm(emptyCreateShiftForm());
                  setCreateModal("shift");
                }}
              >
                + Create shift
              </button>
            </div>
          </div>
          <p className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
            Configure shift timing, flexi/cross-day setup, grace, penalties, and override workflow here.
          </p>
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="max-h-[58vh] overflow-y-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead className="bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-slate-700">Shift</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Timing</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Type</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Breaks</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Grace</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Rotation</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Status</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Default</th>
                    <th className="px-3 py-2 font-semibold text-slate-700 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleShifts.map((shift) => (
                    <tr key={shift.id}>
                      <td className="px-3 py-2 text-slate-800 font-medium">{shift.name || "—"}</td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {(shift.startTime || "—") + " - " + (shift.endTime || "—")}
                        {shift.crossDayShift ? " (cross-day)" : ""}
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {(shift.shiftType || "fixed").toUpperCase()}
                        {shift.flexiEnabled ? " / Flexi" : ""}
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        Paid {shift.paidBreakMinutes ?? 0}m, Unpaid {shift.unpaidBreakMinutes ?? shift.breakMinutes ?? 0}m
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        IN {shift.graceCheckInMinutes ?? 0}m / OUT {shift.graceCheckOutMinutes ?? 0}m
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {shift.shiftRotationEnabled
                          ? `${(shift.shiftRotationPattern || "weekly").replace("_", " ")}${
                              shift.shiftRotationRequiresApproval ? " (approval)" : ""
                            }`
                          : "Off"}
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {(shift.status || "active") === "inactive" ? (
                          <span className="inline-flex px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                            Inactive
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700 text-xs">
                        {shift.isDefault ? (
                          <span className="inline-flex px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Default
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShiftAsDefault(shift.id)}
                            disabled={(shift.status || "active") !== "active"}
                            className="px-2 py-0.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                          >
                            Make default
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="inline-flex p-1.5 border border-slate-200 bg-white rounded-none hover:bg-slate-50"
                          onClick={(e) => openShiftActionMenuFromEvent(e, shift)}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {visibleShifts.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-5 text-center text-slate-500 text-xs">
                        {showInactiveShifts
                          ? "No inactive shifts found."
                          : "No active shifts found. Create your first shift."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
      <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h3 className={`text-base font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}>
          Attendance policies
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative" ref={approvalSettingsRef}>
            <button
              type="button"
              onClick={() => setApprovalSettingsOpen((v) => !v)}
              className="px-3 py-1.5 text-sm rounded-none border bg-white text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
            >
              <Settings size={14} />
              Roles
            </button>
            {approvalSettingsOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 shadow-lg z-20 p-2">
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase border-b mb-2">
                  Roles that can approve policy
                </div>
                {APPROVAL_ROLES.map((role) => (
                  <label key={role.value} className="flex items-center gap-2 px-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={approvalRolesForPolicy.includes(role.value)}
                      onChange={() => toggleApprovalRole(role.value)}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() =>
              printAllPoliciesAsDocument("Attendance Policy", policies, (p) => getPolicySummaryRows(p))
            }
            className="px-3 py-1.5 text-sm rounded-none border bg-white text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <Printer size={14} />
            Print all policies
          </button>
          <input
            type="text"
            value={policySearch}
            onChange={(e) => setPolicySearch(e.target.value)}
            placeholder="Search policy..."
            className={`w-56 p-2 border rounded-none text-sm ${inputCls}`}
          />
          <button
            type="button"
            onClick={() => setShowInactivePolicies((v) => !v)}
            className={`px-3 py-1.5 text-sm rounded-none ${
              darkMode ? "bg-gray-600 text-white" : "bg-gray-100 text-gray-700 border"
            }`}
          >
            {showInactivePolicies ? "Show active" : "Show inactive"}
          </button>
          <button
            type="button"
            onClick={openCreateForm}
            className="px-3 py-1.5 text-sm rh-btn-primary hover:opacity-95 rounded-none"
          >
            + New attendance policy
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="max-h-[58vh] overflow-y-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-700 w-10">#</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Policy</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Effective</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Shift</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Groups</th>
                <th className="px-3 py-2 font-semibold text-slate-700 min-w-[100px]">Applicable</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Status</th>
                <th className="px-3 py-2 font-semibold text-slate-700 w-12"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listedPolicies.map((policy, idx) => {
                const badges = getApplicabilityBadges(policy);
                const shiftNm = shifts.find((s) => String(s.id) === String(policy.shiftGroupId))?.name || "—";
                const grp = [
                  leaveGroups.find((g) => String(g.id) === String(policy.leaveGroupId))?.name,
                  holidayGroups.find((g) => String(g.id) === String(policy.holidayGroupId))?.name,
                  weeklyOffGroups.find((g) => String(g.id) === String(policy.weeklyOffGroupId))?.name,
                ]
                  .filter(Boolean)
                  .join(" / ");
                return (
                  <tr key={policy.id} className="hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-slate-600">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">
                      {policy.policyName || policy.name}
                      <span className="ml-2 text-xs text-slate-500">v{policy.policyVersion ?? 1}</span>
                      {isDemoAttendancePolicy(policy) && (
                        <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600 border border-slate-300">
                          Demo
                        </span>
                      )}
                      {!!(policy.autoAttendance && policy.overrideAttendance) && (
                        <span className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-900 border border-amber-200">
                          Override attendance
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{policy.effectiveDate || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 text-xs">{shiftNm}</td>
                    <td className="px-3 py-2 text-slate-700 text-xs max-w-[180px] truncate" title={grp}>
                      {grp || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        {badges.slice(0, 3).map((b, i) => (
                          <span
                            key={i}
                            className="inline-flex px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700 border border-slate-200"
                          >
                            {b}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs rounded ${
                          policy.status === "active"
                            ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}
                      >
                        {policy.status || "active"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="inline-flex p-1.5 border border-slate-200 bg-white rounded-none hover:bg-slate-50"
                        onClick={(e) => openActionMenuFromEvent(e, policy)}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {policiesSyncError && (
        <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Server sync: {policiesSyncError}. Showing local copy if any.
        </p>
      )}
      {policiesLoading && (
        <p className={`text-sm py-4 text-center ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
          Loading attendance policies…
        </p>
      )}
      {!policiesLoading && listedPolicies.length === 0 && (
        <p className={`text-sm py-6 text-center ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
          {policySearch.trim() ? "No policies match." : "No policies yet. Create one with the button above."}
        </p>
      )}

      {portalToBody(
        isFormOpen && (
        <div
          className="fixed inset-0 z-[12350] flex items-center justify-center overflow-y-auto py-6 px-3 bg-black/45"
          role="dialog"
          aria-modal="true"
          aria-labelledby="attendance-policy-form-title"
        >
          <div
            className={`my-2 flex w-[min(96vw,76rem)] max-h-[min(92vh,940px)] flex-col overflow-hidden rounded-3xl border shadow-[var(--ds-shadow-card)] ${
              darkMode ? "border-slate-600 bg-slate-900 text-slate-100" : "border-[hsl(var(--ds-border))] bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`flex items-center justify-between border-b px-6 py-4 ${
                darkMode ? "border-slate-600" : "border-[hsl(var(--ds-border))]"
              }`}
            >
              <div>
                <h3 id="attendance-policy-form-title" className="text-[15px] font-semibold">
                  {isEditing ? "Edit attendance policy" : "New attendance policy"}
                </h3>
                <p className={`mt-0.5 text-[11px] ${darkMode ? "text-slate-400" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                  5 steps · Default shift on policy · Day-to-day rotation via Change Shift
                </p>
              </div>
              <div className="flex items-center gap-2 relative" ref={policyHelpRef}>
                <button type="button" onClick={() => setPolicyHelpOpen((v) => !v)} title="Guide">
                  <Info size={18} className={darkMode ? "text-slate-400" : "text-gray-500"} />
                </button>
                <button type="button" onClick={handleCancelPolicy}>
                  <X size={18} className={darkMode ? "text-slate-400" : "text-gray-500"} />
                </button>
                {policyHelpOpen && (
                  <div className="absolute right-0 top-8 w-[380px] max-w-[85vw] p-3 bg-white border shadow-lg z-30 text-xs text-gray-700 space-y-1">
                    {getDynamicPolicyGuideLines(newPolicy).map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col">
            <PolicyFormWizardShell
              darkMode={darkMode}
              steps={getAttendancePolicyWizardSteps(newPolicy)}
              currentStep={getAttendancePolicyWizardCurrentId(newPolicy, policyFormStep)}
              onStepClick={(id) => {
                const formStep = mapAttendancePolicyWizardClickToFormStep(newPolicy, id);
                const currentWizardId = getAttendancePolicyWizardCurrentId(
                  newPolicy,
                  policyFormStep
                );
                if (id <= currentWizardId) setPolicyFormStep(formStep);
              }}
              policySaveError={policySaveError}
              footer={
                <>
                  {policyFormStep > 1 && (
                    <button
                      type="button"
                      onClick={goAttendancePolicyPrev}
                      className={`px-4 py-2 text-sm rounded-lg ${darkMode ? "bg-slate-700 text-white" : "bg-slate-100"}`}
                    >
                      Previous
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCancelPolicy}
                    className={`px-4 py-2 text-sm rounded-lg ${darkMode ? "bg-slate-700 text-white" : "bg-slate-100"}`}
                  >
                    Cancel
                  </button>
                  {getNextAttendancePolicyFormStep(newPolicy, policyFormStep) == null && (
                    <button
                      type="button"
                      onClick={() => setFlowchartPolicy(newPolicy)}
                      className="px-3 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700"
                    >
                      Preview flowchart
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={goAttendancePolicyNext}
                    className="px-4 py-2 text-sm rounded-lg rh-btn-primary hover:opacity-95"
                  >
                    {getAttendancePolicyNextButtonLabel(newPolicy, policyFormStep)}
                  </button>
                </>
              }
            >
              {policyFormStep === 1 && (
                <>
              <div className={`p-4 border mb-4 rounded-2xl ${sectionCls}`}>
                <PolicySectionHeading
                  title="Policy basics"
                  tip="Name and effective date identify this policy version for HR and payroll."
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1 font-medium opacity-80">Policy name</label>
                    <input
                      type="text"
                      value={newPolicy.policyName || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, policyName: e.target.value }))}
                      className={`w-full py-2 px-3 border text-sm ${inputCls}`}
                      placeholder="e.g. Standard Office Attendance"
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1 font-medium opacity-80">Effective date</label>
                    <input
                      type="date"
                      value={newPolicy.effectiveDate || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, effectiveDate: e.target.value }))}
                      className={`w-full py-2 px-3 border text-sm ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1 font-medium opacity-80">Status</label>
                    <select
                      value={newPolicy.status === "inactive" ? "inactive" : "active"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, status: e.target.value }))}
                      className={`w-full py-2 px-3 border text-sm ${inputCls}`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 md:mt-6">
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={!!newPolicy.autoAssignOnJoin}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, autoAssignOnJoin: e.target.checked }))}
                    />
                    <span className="text-sm">Auto-assign on joining</span>
                  </label>
                  <div className="md:col-span-2">
                    <label className="block text-xs mb-1 font-medium opacity-80">Description / notes</label>
                    <textarea
                      rows={2}
                      value={newPolicy.policyNotes || newPolicy.description || ""}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, policyNotes: e.target.value, description: e.target.value }))
                      }
                      className={`w-full py-2 px-3 border text-sm ${inputCls}`}
                      placeholder="Optional summary for HR / auditors"
                    />
                  </div>
                </div>
              </div>
                </>
              )}

              {policyFormStep === 2 && (
                <>
                  <p className={`text-[11px] mb-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {isAttendancePolicyOverrideMode(newPolicy) ? (
                      <>
                        Manual Override path: Basics → Check-in → Advanced → Applicability.
                        Weekly Off, Holiday, Leave, and Shift stay in Attendance Settings tabs.
                      </>
                    ) : (
                      <>
                        Check-in channels, capture validation, and punch mode. Cut-off, freeze,
                        payroll export, and compliance are in <strong>Advanced</strong>. Applicability is Step 8.
                      </>
                    )}
                  </p>
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-xs font-semibold uppercase mb-2">Auto attendance & check-in</h4>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={!!newPolicy.autoAttendance}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewPolicy((p) => ({
                            ...p,
                            autoAttendance: checked,
                            overrideAttendance: checked ? !!p.overrideAttendance : false,
                            employeeStatusEditEnabled: checked
                              ? !!p.employeeStatusEditEnabled
                              : false,
                          }));
                          if (!checked) {
                            setPolicyFormStep((step) =>
                              step === 5 || step <= 2 ? step : 2
                            );
                          }
                        }}
                      />
                  Auto attendance (mark all days present)
                </label>
                {!!newPolicy.autoAttendance && (
                  <div
                    className={`mb-3 ml-6 rounded border p-2 ${
                      darkMode ? "border-amber-700/50 bg-amber-950/20" : "border-amber-200 bg-amber-50/80"
                    }`}
                  >
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!!newPolicy.overrideAttendance}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewPolicy((p) => ({
                            ...p,
                            overrideAttendance: checked,
                            ...(checked
                              ? {
                                  checkInMobile: false,
                                  checkInWeb: false,
                                  checkInBiometric: false,
                                  checkInManual: false,
                                  trackLocation: false,
                                  restrictLocationForOnlineCheckIn: false,
                                  enableGeoFencing: false,
                                  enforceIpRestriction: false,
                                  enforceDeviceBinding: false,
                                  allowedIpRanges: "",
                                  autoLogoutEnabled: false,
                                }
                              : {}),
                          }));
                          if (checked) {
                            setPolicyFormStep((step) =>
                              step === 1 || step === 2 || step === 5 ? step : 2
                            );
                          }
                        }}
                      />
                      <span>
                        <span className="text-sm font-medium">Override Attendance</span>
                        <span
                          className={`mt-0.5 block text-[11px] ${
                            darkMode ? "text-slate-400" : "text-slate-600"
                          }`}
                        >
                          Manual marking for employees on this policy: row menu Change Status,
                          working days default Present (WO/PH unchanged), no check-in/out or
                          punch-based PR/LOP. Punch channels and capture rules do not apply.
                          Configure Attendance Cut-Off under Advanced (Step 7).
                        </span>
                      </span>
                    </label>
                  </div>
                )}
                {!!newPolicy.autoAttendance && (
                  <div
                    className={`mt-3 rounded-xl border p-3 ${
                      darkMode
                        ? "border-slate-600 bg-slate-800/40"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <h5 className="text-xs font-semibold mb-1">
                      Employee self-service — Edit Attendance Status
                    </h5>
                    <p className="text-[11px] opacity-80 mb-2">
                      When Auto attendance marks days present, choose whether employees can change
                      day status from ESS (Attendance Logs). Punch-based policies never allow ESS
                      status edit — attendance is driven by check-in/out only.
                    </p>
                    <label className="flex items-start gap-2 mb-2">
                      <input
                        type="checkbox"
                        className="rounded mt-0.5"
                        checked={!!newPolicy.employeeStatusEditEnabled}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            employeeStatusEditEnabled: e.target.checked,
                          }))
                        }
                      />
                      <span className="text-sm">
                        Allow employees to edit or apply for status change
                      </span>
                    </label>
                    {!!newPolicy.employeeStatusEditEnabled && (
                      <div className="ml-6 space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="employeeStatusEditMode"
                            className="rounded-full"
                            checked={!newPolicy.employeeStatusEditRequiresWorkflow}
                            onChange={() =>
                              setNewPolicy((p) => ({
                                ...p,
                                employeeStatusEditRequiresWorkflow: false,
                              }))
                            }
                          />
                          Direct edit (no approval workflow)
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="employeeStatusEditMode"
                            className="rounded-full"
                            checked={newPolicy.employeeStatusEditRequiresWorkflow !== false}
                            onChange={() =>
                              setNewPolicy((p) => ({
                                ...p,
                                employeeStatusEditRequiresWorkflow: true,
                              }))
                            }
                          />
                          Apply for status change (requires approval workflow)
                        </label>
                      </div>
                    )}
                  </div>
                )}
                {!newPolicy.autoAttendance && (
                  <p className="text-[11px] mt-2 opacity-70">
                    Punch-based attendance: employees cannot edit calendar day status from ESS —
                    marks come from check-in/out and policy rules.
                  </p>
                )}
                {isPolicyOverrideAttendance(newPolicy) ? (
                  <p
                    className={`mt-1 text-[11px] ${
                      darkMode ? "text-slate-400" : "text-slate-600"
                    }`}
                  >
                    Check-in channels, location, geo-fence, IP, device binding, and punch mode
                    are hidden — they are not part of Override Attendance.
                  </p>
                ) : (
                <div>
                <div className="flex flex-wrap gap-3 mb-2">
                  {[
                    ["checkInMobile", "Mobile"],
                    ["checkInWeb", "Web"],
                    ["checkInBiometric", "Biometric"],
                  ].map(([k, lab]) => (
                    <label key={k} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={!!newPolicy[k]}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, [k]: e.target.checked }))}
                      />
                      {lab}
                    </label>
                  ))}
                </div>
                <label className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={!!newPolicy.trackLocation}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, trackLocation: e.target.checked }))}
                  />
                  Track location (mobile & web)
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!newPolicy.restrictLocationForOnlineCheckIn}
                    onChange={(e) =>
                      setNewPolicy((p) => ({ ...p, restrictLocationForOnlineCheckIn: e.target.checked }))
                    }
                  />
                  Restrict location for online check-in
                </label>
                {newPolicy.restrictLocationForOnlineCheckIn && (
                  <div className="mt-2 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs opacity-70">Allowed locations</span>
                      <button type="button" className="text-redhalo-navy text-xs" onClick={addAllowedLocation}>
                        + Add
                      </button>
                    </div>
                    {(newPolicy.allowedLocations || []).map((loc, idx) => (
                      <div key={loc.id || idx} className="flex flex-wrap gap-1 items-center">
                        <input
                          placeholder="Name"
                          value={loc.name || ""}
                          onChange={(e) => updateAllowedLocation(idx, "name", e.target.value)}
                          className={`flex-1 min-w-[80px] py-1 px-2 border text-xs ${inputCls}`}
                        />
                        <input
                          placeholder="Address"
                          value={loc.address || ""}
                          onChange={(e) => updateAllowedLocation(idx, "address", e.target.value)}
                          className={`flex-1 min-w-[80px] py-1 px-2 border text-xs ${inputCls}`}
                        />
                        <input
                          type="number"
                          min={0}
                          className={`w-16 py-1 px-2 border text-xs ${inputCls}`}
                          value={loc.radiusMeters ?? 100}
                          onChange={(e) =>
                            updateAllowedLocation(idx, "radiusMeters", Number(e.target.value) || 0)
                          }
                        />
                        <button type="button" className="text-red-600 text-xs" onClick={() => removeAllowedLocation(idx)}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 border-t border-slate-500/20 pt-2 space-y-2">
                  <p className="text-[11px] font-semibold uppercase opacity-80">
                    Capture validation (IP, device, duplicate control)
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.checkInManual}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, checkInManual: e.target.checked }))}
                      />
                      Manual entry allowed
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.enableGeoFencing}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, enableGeoFencing: e.target.checked }))}
                      />
                      Enable geo-fencing
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.enforceIpRestriction}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, enforceIpRestriction: e.target.checked }))
                        }
                      />
                      Restrict by IP
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.enforceDeviceBinding}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, enforceDeviceBinding: e.target.checked }))
                        }
                      />
                      Device binding
                    </label>
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={newPolicy.duplicatePunchControlEnabled !== false}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            duplicatePunchControlEnabled: e.target.checked,
                          }))
                        }
                      />
                      Duplicate punch control
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] opacity-80">Geo-fence radius (meters)</label>
                      <input
                        type="number"
                        min={10}
                        value={newPolicy.geoFenceRadiusMeters ?? 100}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, geoFenceRadiusMeters: Number(e.target.value) || 0 }))
                        }
                        className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] opacity-80">Max bound devices / employee</label>
                      <input
                        type="number"
                        min={1}
                        value={newPolicy.maxBoundDevicesPerEmployee ?? 1}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            maxBoundDevicesPerEmployee: Math.max(1, Number(e.target.value) || 1),
                          }))
                        }
                        className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] opacity-80">Duplicate punch window (minutes)</label>
                      <input
                        type="number"
                        min={0}
                        value={newPolicy.duplicatePunchWindowMinutes ?? 2}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            duplicatePunchWindowMinutes: Number(e.target.value) || 0,
                          }))
                        }
                        className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                      />
                    </div>
                  </div>
                  {newPolicy.enforceIpRestriction && (
                    <div>
                      <label className="text-[10px] opacity-80">Allowed IP / CIDR list (comma-separated)</label>
                      <textarea
                        rows={2}
                        value={newPolicy.allowedIpRanges || ""}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, allowedIpRanges: e.target.value }))}
                        placeholder="e.g. 10.0.0.0/24, 203.122.14.8"
                        className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                      />
                    </div>
                  )}
                </div>
                </div>
                )}
              </div>

              {!isPolicyOverrideAttendance(newPolicy) && (
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-xs font-semibold uppercase mb-2">Punch mode & check-in / out logic</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs opacity-80">Punch scenario</label>
                    <select
                      value={newPolicy.punchMode || "double"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, punchMode: e.target.value }))}
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="single">Single punch</option>
                      <option value="double">Double punch (IN / OUT)</option>
                      <option value="multiple">Multiple punches</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs opacity-80">IN/OUT interpretation</label>
                    <select
                      value={newPolicy.checkInOutRule || "first_in_last_out"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, checkInOutRule: e.target.value }))}
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="first_in_last_out">First IN / Last OUT</option>
                      <option value="strict_pairs">Strict IN-OUT pairing</option>
                      <option value="closest_to_shift">Nearest to shift start/end</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Missing punch handling</label>
                    <select
                      value={newPolicy.missingPunchHandling || "regularization_required"}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, missingPunchHandling: e.target.value }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="regularization_required">Require regularization</option>
                      <option value="auto_mark_default">Auto-mark default attendance</option>
                      <option value="ignore_for_hours">Ignore for hours calc</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Default mark (if auto-mark selected)</label>
                    <select
                      value={newPolicy.defaultMarkingForMissingPunch || "half_day"}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, defaultMarkingForMissingPunch: e.target.value }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="present">Present</option>
                      <option value="half_day">Half day</option>
                      <option value="absent">Absent</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs md:col-span-2">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.autoLogoutEnabled}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, autoLogoutEnabled: e.target.checked }))}
                    />
                    Enable auto logout
                  </label>
                  {newPolicy.autoLogoutEnabled && (
                    <>
                      <div>
                        <label className="text-xs opacity-80">Auto logout trigger</label>
                        <select
                          value={newPolicy.autoLogoutMode || "shift_end_time"}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, autoLogoutMode: e.target.value }))}
                          className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        >
                          <option value="shift_end_time">At shift end time</option>
                          <option value="total_hours">After total worked hours</option>
                          <option value="specific_time">At specific clock time</option>
                        </select>
                      </div>
                      {newPolicy.autoLogoutMode === "total_hours" ? (
                        <div>
                          <label className="text-xs opacity-80">Auto logout after hours</label>
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            value={newPolicy.autoLogoutAfterHours ?? 9}
                            onChange={(e) =>
                              setNewPolicy((p) => ({
                                ...p,
                                autoLogoutAfterHours: Number(e.target.value) || 0,
                              }))
                            }
                            className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                          />
                        </div>
                      ) : newPolicy.autoLogoutMode === "specific_time" ? (
                        <div>
                          <label className="text-xs opacity-80">Auto logout time (IST)</label>
                          <input
                            type="time"
                            value={newPolicy.autoLogoutTime || "23:59"}
                            onChange={(e) => setNewPolicy((p) => ({ ...p, autoLogoutTime: e.target.value }))}
                            className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                          />
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
              )}
                </>
              )}

              {policyFormStep === 2 && !isAttendancePolicyOverrideMode(newPolicy) && (
                <>

              <div className={`p-4 border rounded-2xl mb-4 ${sectionCls}`}>
                <PolicySectionHeading
                  title="Grace & late / early"
                  tip="Grace minutes before late/early flags. Penalties apply after grace is used up."
                />
                <label className="flex items-center gap-2 mb-2 text-xs">
                  <input
                    type="checkbox"
                    checked={newPolicy.graceRulesEnabled !== false}
                    onChange={(e) =>
                      setNewPolicy((p) => ({ ...p, graceRulesEnabled: e.target.checked }))
                    }
                  />
                  Enable grace & late/early rules
                </label>
                {newPolicy.graceRulesEnabled !== false && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        ["graceCheckInMinutes", "Grace in (min)"],
                        ["graceCheckOutMinutes", "Grace out (min)"],
                        ["considerLateAfterMinutes", "Late after (min)"],
                        ["considerEarlyBeforeMinutes", "Early before (min)"],
                      ].map(([k, lab]) => (
                        <div key={k}>
                          <label className="text-[10px] opacity-80">{lab}</label>
                          <input
                            type="number"
                            min={0}
                            value={newPolicy[k] ?? 0}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, [k]: Number(e.target.value) || 0 }))
                            }
                            className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                      <div>
                        <label className="text-[10px] opacity-80">Grace allowance period</label>
                        <select
                          value={newPolicy.graceOccurrenceWindow || "monthly"}
                          onChange={(e) =>
                            setNewPolicy((p) => ({ ...p, graceOccurrenceWindow: e.target.value }))
                          }
                          className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] opacity-80">Allowed late grace count</label>
                        <input
                          type="number"
                          min={0}
                          value={newPolicy.lateGraceAllowedOccurrences ?? 0}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              lateGraceAllowedOccurrences: Number(e.target.value) || 0,
                            }))
                          }
                          className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] opacity-80">Allowed early grace count</label>
                        <input
                          type="number"
                          min={0}
                          value={newPolicy.earlyGraceAllowedOccurrences ?? 0}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              earlyGraceAllowedOccurrences: Number(e.target.value) || 0,
                            }))
                          }
                          className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="text-[10px] opacity-80">Early login buffer (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={newPolicy.earlyLoginBufferMinutes ?? 0}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              earlyLoginBufferMinutes: Number(e.target.value) || 0,
                            }))
                          }
                          className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] opacity-80">Late logout buffer (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={newPolicy.lateLogoutBufferMinutes ?? 0}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              lateLogoutBufferMinutes: Number(e.target.value) || 0,
                            }))
                          }
                          className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        />
                      </div>
                    </div>

                    <div className="mt-4 border-t border-[hsl(var(--ds-border))] pt-3">
                      <p className="mb-2 text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))]">
                        Late arrival &amp; early departure penalty
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium opacity-80">Late arrival</label>
                          <select
                            value={newPolicy.latePenaltyType || "none"}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, latePenaltyType: e.target.value }))
                            }
                            className={`mt-1 w-full rounded-xl border px-2 py-1.5 text-sm ${inputCls}`}
                          >
                            {SHIFT_PENALTY_TYPES.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          {newPolicy.latePenaltyType === "minutes_lop" && (
                            <input
                              type="number"
                              min={0}
                              placeholder="Minutes LOP"
                              value={newPolicy.latePenaltyMinutes ?? 0}
                              onChange={(e) =>
                                setNewPolicy((p) => ({
                                  ...p,
                                  latePenaltyMinutes: Number(e.target.value) || 0,
                                }))
                              }
                              className={`mt-2 w-full rounded-xl border px-2 py-1.5 text-sm ${inputCls}`}
                            />
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium opacity-80">Early departure</label>
                          <select
                            value={newPolicy.earlyDeparturePenaltyType || "none"}
                            onChange={(e) =>
                              setNewPolicy((p) => ({
                                ...p,
                                earlyDeparturePenaltyType: e.target.value,
                              }))
                            }
                            className={`mt-1 w-full rounded-xl border px-2 py-1.5 text-sm ${inputCls}`}
                          >
                            {SHIFT_PENALTY_TYPES.map((o) => (
                              <option key={`e-${o.value}`} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          {newPolicy.earlyDeparturePenaltyType === "minutes_lop" && (
                            <input
                              type="number"
                              min={0}
                              placeholder="Minutes LOP"
                              value={newPolicy.earlyDeparturePenaltyMinutes ?? 0}
                              onChange={(e) =>
                                setNewPolicy((p) => ({
                                  ...p,
                                  earlyDeparturePenaltyMinutes: Number(e.target.value) || 0,
                                }))
                              }
                              className={`mt-2 w-full rounded-xl border px-2 py-1.5 text-sm ${inputCls}`}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>


              <div className={`p-4 border rounded-2xl mb-4 ${sectionCls}`}>
                <PolicySectionHeading
                  title="Day marking (hours)"
                  tip="Full-day / half-day thresholds from worked hours after break."
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <div>
                    <label className="block text-xs mb-0.5 opacity-80">Full day if hours ≥</label>
                    <input
                      type="number"
                      min={0}
                      step={0.25}
                      value={newPolicy.fullDayIfHoursAtLeast ?? newPolicy.fullDayHours ?? 8}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          fullDayIfHoursAtLeast: Number(e.target.value) || 0,
                          fullDayHours: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.autoAbsentMarkingEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, autoAbsentMarkingEnabled: e.target.checked }))
                      }
                    />
                    Enable auto absent marking
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.autoHalfDayMarkingEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, autoHalfDayMarkingEnabled: e.target.checked }))
                      }
                    />
                    Enable auto half-day marking
                  </label>
                  <div>
                    <label className="text-xs opacity-80">Absent if hours below</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={newPolicy.absentMarkIfHoursBelow ?? 2}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, absentMarkIfHoursBelow: Number(e.target.value) || 0 }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Half-day if hours below</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={newPolicy.halfDayMarkIfHoursBelow ?? 4}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, halfDayMarkIfHoursBelow: Number(e.target.value) || 0 }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.excludeOnDutyFromAutoMarking !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          excludeOnDutyFromAutoMarking: e.target.checked,
                        }))
                      }
                    />
                    Exclude On-Duty (OD) from auto marking
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.excludeWfhFromAutoMarking !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          excludeWfhFromAutoMarking: e.target.checked,
                        }))
                      }
                    />
                    Exclude Work From Home from auto marking
                  </label>
                </div>
              </div>

                </>
              )}

              {policyFormStep === 3 && (
                <>
              {/* was step 4 Requests — merged with Groups below */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <PolicySectionHeading
                  title="Regularization"
                  tip="How long after the attendance date employees can request punch corrections."
                />
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={!!newPolicy.regularizationAllowed}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, regularizationAllowed: e.target.checked }))}
                  />
                  Regularization allowed
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="text-xs opacity-80">Max days after attendance date</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.regularizationTillDays ?? 7}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, regularizationTillDays: Number(e.target.value) || 0 }))
                      }
                      className={`w-full py-1 px-2 border text-sm mt-0.5 ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Max calendar days back (from today)</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.regularizationMaxCalendarDaysBack ?? 90}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          regularizationMaxCalendarDaysBack: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm mt-0.5 ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Locked payroll months back (0 = current only)</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.regularizationLockedMonthsMax ?? 2}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          regularizationLockedMonthsMax: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm mt-0.5 ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Max punch corrections per employee / month</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.regularizationMaxPunchCorrectionsPerMonth ?? 10}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          regularizationMaxPunchCorrectionsPerMonth: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm mt-0.5 ${inputCls}`}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs opacity-80">Max regularization request instances per employee / month</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.regularizationMaxRequestInstancesPerMonth ?? 5}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          regularizationMaxRequestInstancesPerMonth: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm mt-0.5 ${inputCls}`}
                    />
                  </div>
                </div>
              </div>

              {/* Regularization workflow controls (grouped with Requests) */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">Regularization Workflow Controls</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium opacity-80">Regularization cut-off day (month)</label>
                    <input type="number" min={1} max={31}
                      value={newPolicy.regularizationCutOffDayOfMonth ?? 5}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, regularizationCutOffDayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)) }))}
                      className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium opacity-80">Approval levels</label>
                    <input type="number" min={1} max={5}
                      value={newPolicy.regularizationApprovalLevels ?? 2}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, regularizationApprovalLevels: Math.min(5, Math.max(1, Number(e.target.value) || 1)) }))}
                      className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs mt-6">
                    <input type="checkbox" className="rounded"
                      checked={newPolicy.regularizationRequireReason !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, regularizationRequireReason: e.target.checked }))}
                    />
                    Reason mandatory for regularization
                  </label>
                </div>
              </div>

              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <PolicySectionHeading
                  title="Punch day assignment"
                  tip="Controls how IN/OUT punches map to an attendance calendar day across midnight and between shifts. Example: OUT 21:30 then IN 14:00 next day becomes next-day check-in when the rest gap meets Min hours between shifts."
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium opacity-80 inline-flex items-center gap-1">
                      Punch day mode
                      <span title="Auto uses shift window + rest gap. Shift window follows overnight shift. Calendar date always uses clock date.">ⓘ</span>
                    </label>
                    <select
                      value={newPolicy.punchDayMode || "auto"}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, punchDayMode: e.target.value }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                    >
                      <option value="auto">Auto (recommended)</option>
                      <option value="shift_window">Shift window</option>
                      <option value="calendar_date">Calendar date</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium opacity-80 inline-flex items-center gap-1">
                      Min hours between shifts
                      <span title="After a closed OUT, the next punch is treated as a new day's IN when this rest gap has elapsed.">ⓘ</span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={newPolicy.minHoursBetweenShifts ?? 8}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          minHoursBetweenShifts: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium opacity-80 inline-flex items-center gap-1">
                      Day-end check (hours after shift end)
                      <span title="After this many hours past shift end, an open session is auto-closed (timer resets) with OUT set to shift end. Day and night shifts both use shift end — not midnight. Default 8.">
                        ⓘ
                      </span>
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={newPolicy.dayEndCheckHours ?? 8}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          dayEndCheckHours: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                    />
                  </div>
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={newPolicy.overnightOutAttachesToInDay !== false}
                    onChange={(e) =>
                      setNewPolicy((p) => ({
                        ...p,
                        overnightOutAttachesToInDay: e.target.checked,
                      }))
                    }
                  />
                  Overnight OUT stays on the IN attendance day
                </label>
                <div className="mt-3 max-w-sm">
                  <label className="text-xs font-medium opacity-80 inline-flex items-center gap-1">
                    Missing attendance action
                    <span title="When Run Attendance finds no punches on a working day.">ⓘ</span>
                  </label>
                  <select
                    value={newPolicy.missingAttendanceAction || "lop"}
                    onChange={(e) =>
                      setNewPolicy((p) => ({
                        ...p,
                        missingAttendanceAction: e.target.value,
                      }))
                    }
                    className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                  >
                    <option value="lop">Mark LOP</option>
                    <option value="absent">Mark Absent</option>
                    <option value="exception">Exception only</option>
                    <option value="leave_required">Leave required</option>
                  </select>
                </div>
              </div>

              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">ESS request features</h4>
                <p className="text-[11px] opacity-70 mb-3">
                  Controls which request types appear for employees matched to this policy
                  (Request desk, My Team, Admin attendance actions).
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.changeShiftEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, changeShiftEnabled: e.target.checked }))
                      }
                    />
                    Change Shift enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.shortLeaveEnabled}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, shortLeaveEnabled: e.target.checked }))
                      }
                    />
                    Short Leave enabled
                  </label>
                  {!!newPolicy.shortLeaveEnabled && (
                    <div className="md:col-span-2">
                      <label className="text-xs opacity-80">Short leave max minutes</label>
                      <input
                        type="number"
                        min={1}
                        max={480}
                        value={newPolicy.shortLeaveMaxMinutes ?? 60}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            shortLeaveMaxMinutes: Math.min(
                              480,
                              Math.max(1, Number(e.target.value) || 60)
                            ),
                          }))
                        }
                        className={`w-40 py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">OD / WFH requests</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.odRequestEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, odRequestEnabled: e.target.checked }))}
                    />
                    OD request enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.wfhRequestEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, wfhRequestEnabled: e.target.checked }))}
                    />
                    WFH request enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.odWfhRequireApproval !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, odWfhRequireApproval: e.target.checked }))
                      }
                    />
                    OD / WFH requires approval
                  </label>
                  <div>
                    <label className="text-xs opacity-80">Tracking mode</label>
                    <select
                      value={newPolicy.odWfhTrackingMode || "hours"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, odWfhTrackingMode: e.target.value }))}
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="hours">Hours based</option>
                      <option value="full_day">Full day only</option>
                      <option value="slot">Time-slot based</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-xs font-semibold uppercase mb-2">Leave & attendance integration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs opacity-80">Priority rule</label>
                    <select
                      value={newPolicy.leaveAttendancePriorityRule || "approved_leave_first"}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          leaveAttendancePriorityRule: e.target.value,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="approved_leave_first">Approved leave overrides attendance</option>
                      <option value="attendance_first">Attendance punches override leave</option>
                      <option value="hybrid">Hybrid with manual override</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs mt-5">
                    <input
                      type="checkbox"
                      checked={newPolicy.halfDayLeaveMappingEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          halfDayLeaveMappingEnabled: e.target.checked,
                        }))
                      }
                    />
                    Enable half-day leave mapping
                  </label>
                </div>
              </div>
                </>
              )}

              {policyFormStep === 3 && (
                <>
              <div className={`mb-4 grid grid-cols-1 gap-2 rounded-2xl border p-4 md:grid-cols-2 ${sectionCls}`}>
                <PolicySectionHeading
                  className="col-span-full mb-1"
                  title="Shift & calendar groups"
                  tip="Policy Shift is the default clock template. To move someone General → another shift next month, use Change Shift (or day override)—not a new policy."
                />
                <p className="col-span-full mb-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  Default shift for everyone on this policy. Month-to-month rotation is managed with{" "}
                  <strong>Change Shift</strong> requests (or Admin day override), not by editing this dropdown each month.
                </p>
                {[
                  ["shiftGroupId", "Shift", shifts, "shift"],
                  ["leaveGroupId", "Leave group", leaveGroups, "leave"],
                  ["holidayGroupId", "Holiday group", holidayGroups, "holiday"],
                  ["weeklyOffGroupId", "Weekly off group", weeklyOffGroups, "weeklyoff"],
                ].map(([field, label, list, cm]) => (
                  <div key={field} className="flex gap-1 items-end">
                    <div className="flex-1">
                      <label className="block text-xs mb-0.5 opacity-80">{label}</label>
                      <select
                        value={newPolicy[field] || ""}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, [field]: e.target.value }))}
                        className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                      >
                        <option value="">Select…</option>
                        {list.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {cm !== "shift" && (
                      <button
                        type="button"
                        className="px-2 py-2 border rounded text-sm shrink-0"
                        onClick={() => {
                          setCreateModal(cm);
                        }}
                        title={`Create ${label}`}
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
                <div className="col-span-full border-t border-slate-500/20 pt-3 mt-2">
                  <h5 className="text-xs font-semibold uppercase mb-2 text-slate-600 dark:text-slate-300">
                    Compensatory off &amp; overtime (policy-level)
                  </h5>
                  <label className="flex items-center gap-2 mb-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.compensatoryOffEnabled}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, compensatoryOffEnabled: e.target.checked }))
                      }
                    />
                    Track compensatory off / OT under this policy
                  </label>
                  <div className="mb-2">
                    <label className="text-xs opacity-80 inline-flex items-center gap-1">
                      Comp Off credit mode
                      <span title="Prior: after approved work hours, auto-credit then employee avails. Post: credit needs approval after work, then avail.">ⓘ</span>
                    </label>
                    <select
                      value={newPolicy.compOffCreditMode || "post"}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, compOffCreditMode: e.target.value }))
                      }
                      className={`w-full md:w-72 py-1.5 px-2 border text-sm mt-0.5 ${inputCls}`}
                    >
                      <option value="prior">Prior approval then auto credit</option>
                      <option value="post">Post work — approve credit then avail</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 mb-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.overtimeAutoApproveToPayroll}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          overtimeAutoApproveToPayroll: e.target.checked,
                        }))
                      }
                    />
                    Auto-approve calculated OT to payroll
                    <span title="Only when OT pre-approval is not required. Otherwise OT stays Calculated until approved.">ⓘ</span>
                  </label>
                  <div className="mb-2">
                    <label className="text-xs opacity-80">Mode</label>
                    <select
                      value={newPolicy.otCompOffMode || "none"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, otCompOffMode: e.target.value }))}
                      className={`w-full md:w-72 py-1.5 px-2 border text-sm mt-0.5 ${inputCls}`}
                    >
                      <option value="none">None</option>
                      <option value="ot_only">Overtime pay only</option>
                      <option value="comp_off_only">Compensatory off only</option>
                      <option value="both">Both OT pay and comp off (as per rules)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.overtimeEnabled}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, overtimeEnabled: e.target.checked }))
                        }
                      />
                      Overtime eligibility enabled
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!newPolicy.overtimeRequiresPreApproval}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, overtimeRequiresPreApproval: e.target.checked }))
                        }
                      />
                      OT requires pre-approval
                    </label>
                    <div>
                      <label className="text-[10px] opacity-80">OT min threshold (minutes)</label>
                      <input
                        type="number"
                        min={0}
                        value={newPolicy.overtimeMinThresholdMinutes ?? 30}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            overtimeMinThresholdMinutes: Number(e.target.value) || 0,
                          }))
                        }
                        className={`w-full py-1 border text-sm ${inputCls}`}
                      />
                    </div>
                  </div>
                  <textarea
                    placeholder="General conditions (e.g. pre-approval required for Sunday work, blackout dates…)"
                    value={newPolicy.compensatoryGeneralConditions || ""}
                    onChange={(e) =>
                      setNewPolicy((p) => ({ ...p, compensatoryGeneralConditions: e.target.value }))
                    }
                    rows={2}
                    className={`w-full py-1.5 px-2 border text-sm mb-2 ${inputCls}`}
                  />
                  {(newPolicy.otCompOffMode === "ot_only" || newPolicy.otCompOffMode === "both") && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-[10px] opacity-80">OT minimum hours / day</label>
                        <input
                          type="number"
                          min={0}
                          step={0.25}
                          value={newPolicy.otMinHoursPerDay ?? 1}
                          onChange={(e) =>
                            setNewPolicy((p) => ({ ...p, otMinHoursPerDay: Number(e.target.value) || 0 }))
                          }
                          className={`w-full py-1 border text-sm ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] opacity-80">Pay multiplier (e.g. 1.5)</label>
                        <input
                          type="number"
                          min={1}
                          step={0.25}
                          value={newPolicy.overtimeRateMultiplier ?? newPolicy.otPayMultiplier ?? 1.5}
                          onChange={(e) =>
                            setNewPolicy((p) => {
                              const multiplier = Number(e.target.value) || 1;
                              return { ...p, overtimeRateMultiplier: multiplier, otPayMultiplier: multiplier };
                            })
                          }
                          className={`w-full py-1 border text-sm ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] opacity-80">Daily OT cap (hours)</label>
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          value={newPolicy.overtimeDailyCapHours ?? 4}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              overtimeDailyCapHours: Number(e.target.value) || 0,
                            }))
                          }
                          className={`w-full py-1 border text-sm ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] opacity-80">Monthly OT cap (hours)</label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={newPolicy.overtimeMonthlyCapHours ?? 60}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              overtimeMonthlyCapHours: Number(e.target.value) || 0,
                            }))
                          }
                          className={`w-full py-1 border text-sm ${inputCls}`}
                        />
                      </div>
                    </div>
                  )}
                  {(newPolicy.otCompOffMode === "comp_off_only" || newPolicy.otCompOffMode === "both") && (
                    <div className="space-y-2 mb-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] opacity-80">Comp off — minimum credit hours</label>
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            value={newPolicy.compOffMinHours ?? 4}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, compOffMinHours: Number(e.target.value) || 0 }))
                            }
                            className={`w-full py-1 border text-sm ${inputCls}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] opacity-80">Comp off — expiry (days)</label>
                          <input
                            type="number"
                            min={0}
                            value={newPolicy.compOffExpiryDays ?? 90}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, compOffExpiryDays: Number(e.target.value) || 0 }))
                            }
                            className={`w-full py-1 border text-sm ${inputCls}`}
                          />
                        </div>
                      </div>
                      <p className="text-[10px] opacity-80">Eligible attendance statuses for earning comp off</p>
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={newPolicy.compOffEligiblePresent !== false}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, compOffEligiblePresent: e.target.checked }))
                            }
                          />
                          Extra hours on present / full day
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={!!newPolicy.compOffEligibleWeekOffWorked}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, compOffEligibleWeekOffWorked: e.target.checked }))
                            }
                          />
                          Worked on weekly off
                        </label>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={!!newPolicy.compOffEligibleHolidayWorked}
                            onChange={(e) =>
                              setNewPolicy((p) => ({ ...p, compOffEligibleHolidayWorked: e.target.checked }))
                            }
                          />
                          Worked on holiday
                        </label>
                      </div>
                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={!!newPolicy.compOffRequiresApproval}
                          onChange={(e) =>
                            setNewPolicy((p) => ({ ...p, compOffRequiresApproval: e.target.checked }))
                          }
                        />
                        Comp off credit / availing requires approval (uses leave workflow above when enabled)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <div>
                          <label className="text-[10px] opacity-80">Accrual unit (hours)</label>
                          <input
                            type="number"
                            min={0.5}
                            step={0.5}
                            value={newPolicy.compOffAccrualUnitHours ?? 1}
                            onChange={(e) =>
                              setNewPolicy((p) => ({
                                ...p,
                                compOffAccrualUnitHours: Number(e.target.value) || 1,
                              }))
                            }
                            className={`w-full py-1 border text-sm ${inputCls}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] opacity-80">Max accrual days / month</label>
                          <input
                            type="number"
                            min={0}
                            step={0.5}
                            value={newPolicy.compOffMaxAccrualDaysPerMonth ?? 2}
                            onChange={(e) =>
                              setNewPolicy((p) => ({
                                ...p,
                                compOffMaxAccrualDaysPerMonth: Number(e.target.value) || 0,
                              }))
                            }
                            className={`w-full py-1 border text-sm ${inputCls}`}
                          />
                        </div>
                        <label className="flex items-center gap-2 text-xs mt-5">
                          <input
                            type="checkbox"
                            checked={!!newPolicy.compOffEncashmentEnabled}
                            onChange={(e) =>
                              setNewPolicy((p) => ({
                                ...p,
                                compOffEncashmentEnabled: e.target.checked,
                              }))
                            }
                          />
                          Encashment enabled
                        </label>
                        <div>
                          <label className="text-[10px] opacity-80">Encashment rate</label>
                          <input
                            type="number"
                            min={0}
                            step={0.25}
                            value={newPolicy.compOffEncashmentRate ?? 1}
                            onChange={(e) =>
                              setNewPolicy((p) => ({
                                ...p,
                                compOffEncashmentRate: Number(e.target.value) || 0,
                              }))
                            }
                            className={`w-full py-1 border text-sm ${inputCls}`}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
                  {/* OT / comp-off notes moved to Step 7 (Advanced) */}
                </>
              )}

              {policyFormStep === 4 && (
                <>
              <ActivityWorkflowsPolicySection
                darkMode={darkMode}
                activityWorkflows={newPolicy.activityWorkflows}
                onChange={(next) =>
                  setNewPolicy((p) => ({ ...p, activityWorkflows: next }))
                }
              />
                </>
              )}

              {policyFormStep === 5 && (
                <>
              {/* ── Card 0: Attendance Cut-Off (LOP deferral) ── */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <PolicySectionHeading
                  title="Attendance cut-off (LOP deferral)"
                  tip={cutOffExampleText(newPolicy.cutOffDay ?? newPolicy.cutOffToDay ?? 25)}
                />
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={!!newPolicy.cutOffEnabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setNewPolicy((p) => ({
                        ...p,
                        cutOffEnabled: on,
                        cutOffMethod: on ? "specific" : "calendar_month",
                        cutOffDay: p.cutOffDay ?? p.cutOffToDay ?? 25,
                        cutOffToDay: p.cutOffDay ?? p.cutOffToDay ?? 25,
                      }));
                    }}
                  />
                  <span className="text-sm font-medium">Enable attendance cut-off</span>
                </label>

                {!!newPolicy.cutOffEnabled && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-700">
                          Cut-off day of month (1–28)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={newPolicy.cutOffDay ?? newPolicy.cutOffToDay ?? 25}
                          onChange={(e) => {
                            const day = Math.min(28, Math.max(1, Number(e.target.value) || 1));
                            setNewPolicy((p) => ({
                              ...p,
                              cutOffDay: day,
                              cutOffToDay: day,
                              cutOffMethod: "specific",
                            }));
                          }}
                          className={`w-full py-1.5 px-2 border rounded-lg text-sm mt-1 ${inputCls}`}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">
                          New joiner handling
                        </label>
                        <select
                          value={newPolicy.cutOffNewJoinerHandling || "defer_lop"}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              cutOffNewJoinerHandling: e.target.value,
                            }))
                          }
                          className={`w-full py-1.5 px-2 border rounded-lg text-sm mt-1 ${inputCls}`}
                        >
                          <option value="defer_lop">
                            Hold payable &amp; auto reverse next month
                          </option>
                          <option value="block_joining">
                            Block joining after cut-off
                          </option>
                          <option value="no_adjustment">
                            Deduct all LOP in joining month
                          </option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">
                          Separation handling
                        </label>
                        <select
                          value={newPolicy.cutOffSeparationHandling || "fnf_recovery"}
                          onChange={(e) =>
                            setNewPolicy((p) => ({
                              ...p,
                              cutOffSeparationHandling: e.target.value,
                            }))
                          }
                          className={`w-full py-1.5 px-2 border rounded-lg text-sm mt-1 ${inputCls}`}
                        >
                          <option value="fnf_recovery">
                            Recover post-cut-off LOP in F&amp;F
                          </option>
                          <option value="current_month_deduct">
                            Deduct all LOP in leaving month
                          </option>
                          <option value="no_recovery">No special recovery</option>
                        </select>
                      </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={newPolicy.cutOffExcessPayRecovery !== false}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            cutOffExcessPayRecovery: e.target.checked,
                          }))
                        }
                      />
                      Settle pending deferred LOP in F&amp;F on final settlement
                    </label>
                  </div>
                )}
              </div>

              <details className={`mb-4 rounded-xl border ${sectionCls}`}>
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold">
                  More options (sandwich, locks, compliance)
                </summary>
                <div className="space-y-4 border-t px-4 py-3">
              {/* ── Sandwich Rule ── */}
              <div className={`p-4 border rounded-xl ${sectionCls}`}>
                <PolicySectionHeading
                  title="Sandwich rule"
                  tip="Optionally count weekly off / holiday between leave days as leave."
                />
                <label className="flex items-center gap-2 mb-2">
                  <input type="checkbox" className="rounded"
                    checked={!!newPolicy.sandwichRuleEnabled}
                    onChange={(e) => setNewPolicy((p) => ({ ...p, sandwichRuleEnabled: e.target.checked }))}
                  />
                  <span className="text-sm">Enable sandwich rule</span>
                </label>
                {newPolicy.sandwichRuleEnabled && (
                  <>
                    <div className="flex flex-wrap gap-4 mb-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="rounded"
                          checked={newPolicy.sandwichRuleWeeklyOff !== false}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, sandwichRuleWeeklyOff: e.target.checked }))}
                        />
                        Apply between weekly off(s)
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" className="rounded"
                          checked={newPolicy.sandwichRuleHoliday !== false}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, sandwichRuleHoliday: e.target.checked }))}
                        />
                        Apply around holiday(s)
                      </label>
                    </div>
                    <textarea placeholder="Optional notes (e.g. excludes optional holidays, max consecutive days…)"
                      value={newPolicy.sandwichRuleNotes || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, sandwichRuleNotes: e.target.value }))}
                      rows={2} className={`w-full py-1.5 px-2 border rounded-lg text-sm ${inputCls}`}
                    />
                  </>
                )}
              </div>

              {!isAttendancePolicyOverrideMode(newPolicy) && (
                <>
              {/* ── Card 3: Alerts & month-end freeze ── */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">Alerts &amp; month-end freeze</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs opacity-80">Freeze edits N days before month-end (0 = off)</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.attendanceFreezeDaysBeforeMonthEnd ?? 0}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          attendanceFreezeDaysBeforeMonthEnd: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Missed punch reminder after (hours from shift start)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={newPolicy.missedPunchReminderAfterHours ?? 4}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          missedPunchReminderAfterHours: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                </div>
              </div>

              {/* ── Card 4: Penalty & deduction engine ── */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">Penalty &amp; deduction engine</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.penaltyEngineEnabled}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, penaltyEngineEnabled: e.target.checked }))}
                    />
                    Enable rule-based penalty engine
                  </label>
                  <div>
                    <label className="text-xs opacity-80">Reset cycle</label>
                    <select
                      value={newPolicy.penaltyResetCycle || "monthly"}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, penaltyResetCycle: e.target.value }))}
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs opacity-80">Penalty rules notes / matrix</label>
                    <textarea
                      rows={2}
                      placeholder="e.g. 4th late in month -> half-day; repeated early exits -> LOP"
                      value={newPolicy.penaltyRuleNotes || ""}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, penaltyRuleNotes: e.target.value }))}
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                </div>
              </div>

              {/* ── Card 5: Payroll integration outputs ── */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">Payroll integration outputs</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.payrollIntegrationEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, payrollIntegrationEnabled: e.target.checked }))
                      }
                    />
                    Enable payroll integration
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.payrollExportIncludePresentDays !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          payrollExportIncludePresentDays: e.target.checked,
                        }))
                      }
                    />
                    Export present days
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.payrollExportIncludePaidDays !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          payrollExportIncludePaidDays: e.target.checked,
                        }))
                      }
                    />
                    Export paid days
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.payrollExportIncludeOtHours !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          payrollExportIncludeOtHours: e.target.checked,
                        }))
                      }
                    />
                    Export OT hours
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.payrollExportIncludeLopDays !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          payrollExportIncludeLopDays: e.target.checked,
                        }))
                      }
                    />
                    Export LOP days
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.payrollLockEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, payrollLockEnabled: e.target.checked }))}
                    />
                    Payroll lock enabled
                  </label>
                  <div>
                    <label className="text-xs opacity-80">Payroll lock day of month</label>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={newPolicy.payrollLockDayOfMonth ?? 2}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          payrollLockDayOfMonth: Math.min(31, Math.max(1, Number(e.target.value) || 1)),
                        }))
                      }
                      className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                </div>
              </div>

              {/* ── Card 6: Notifications, escalation & reports ── */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">Notifications, escalation &amp; reports</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.notificationsEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, notificationsEnabled: e.target.checked }))}
                    />
                    Notifications enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.notifyMissedPunch !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, notifyMissedPunch: e.target.checked }))}
                    />
                    Missed punch alerts
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.notifyLateComing !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, notifyLateComing: e.target.checked }))}
                    />
                    Late coming alerts
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.notifyEarlyExit !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, notifyEarlyExit: e.target.checked }))}
                    />
                    Early exit alerts
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.notifyRegularizationPending !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, notifyRegularizationPending: e.target.checked }))
                      }
                    />
                    Pending regularization alerts
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.escalationEnabled}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, escalationEnabled: e.target.checked }))}
                    />
                    Escalation enabled
                  </label>
                  <div>
                    <label className="text-xs opacity-80">Escalate after (hours)</label>
                    <input
                      type="number"
                      min={1}
                      value={newPolicy.escalationAfterHours ?? 24}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, escalationAfterHours: Number(e.target.value) || 1 }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.dashboardMusterRollEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, dashboardMusterRollEnabled: e.target.checked }))
                      }
                    />
                    Muster roll report
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.dashboardExceptionReportEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          dashboardExceptionReportEnabled: e.target.checked,
                        }))
                      }
                    />
                    Exception report
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.dashboardAttendanceLogEnabled !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, dashboardAttendanceLogEnabled: e.target.checked }))
                      }
                    />
                    Attendance logs report
                  </label>
                </div>
              </div>

              {/* ── Card 7: Compliance & system controls ── */}
              <div className={`p-4 border rounded-xl mb-4 ${sectionCls}`}>
                <h4 className="text-sm font-semibold mb-3">Compliance &amp; system controls</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs opacity-80">Daily working-hour cap</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={newPolicy.complianceWorkingHoursCapPerDay ?? 12}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          complianceWorkingHoursCapPerDay: Number(e.target.value) || 0,
                        }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs opacity-80">Weekly hours cap</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={newPolicy.complianceWeeklyHoursCap ?? 60}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, complianceWeeklyHoursCap: Number(e.target.value) || 0 }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs mt-5">
                    <input
                      type="checkbox"
                      checked={newPolicy.complianceOvertimeRequiresConsent !== false}
                      onChange={(e) =>
                        setNewPolicy((p) => ({
                          ...p,
                          complianceOvertimeRequiresConsent: e.target.checked,
                        }))
                      }
                    />
                    OT requires consent
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.attendanceLockEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, attendanceLockEnabled: e.target.checked }))}
                    />
                    Attendance lock enabled
                  </label>
                  <div>
                    <label className="text-xs opacity-80">Attendance lock after (days)</label>
                    <input
                      type="number"
                      min={0}
                      value={newPolicy.attendanceLockAfterDays ?? 3}
                      onChange={(e) =>
                        setNewPolicy((p) => ({ ...p, attendanceLockAfterDays: Number(e.target.value) || 0 }))
                      }
                      className={`w-full py-1 px-2 border text-sm ${inputCls}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.auditTrailEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, auditTrailEnabled: e.target.checked }))}
                    />
                    Audit trail enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={newPolicy.bulkUploadEnabled !== false}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, bulkUploadEnabled: e.target.checked }))}
                    />
                    Bulk upload enabled
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!newPolicy.apiIntegrationEnabled}
                      onChange={(e) => setNewPolicy((p) => ({ ...p, apiIntegrationEnabled: e.target.checked }))}
                    />
                    API integration enabled
                  </label>
                  {newPolicy.apiIntegrationEnabled && (
                    <div className="md:col-span-3">
                      <label className="text-xs opacity-80">API integration notes</label>
                      <textarea
                        rows={2}
                        value={newPolicy.apiIntegrationNotes || ""}
                        onChange={(e) => setNewPolicy((p) => ({ ...p, apiIntegrationNotes: e.target.value }))}
                        className={`w-full py-1.5 px-2 border text-sm ${inputCls}`}
                        placeholder="Endpoint, auth mechanism, payload mapping notes..."
                      />
                    </div>
                  )}
                </div>
              </div>

                </>
              )}

                </div>
              </details>

                </>
              )}

              {policyFormStep === 5 && (
                <>
                  {/* Applicability continues on same step as cut-off */}
                  <div className={`p-4 border rounded-xl ${sectionCls}`}>
                    <PolicySectionHeading
                      title="Applicability"
                      tip="Who this policy covers. Empty filters (or All employees) apply broadly."
                    />
                    <label className="flex items-center gap-2 mb-3 text-sm">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={!!newPolicy.allEmployees}
                        onChange={(e) =>
                          setNewPolicy((p) => ({
                            ...p,
                            allEmployees: e.target.checked,
                            ...(e.target.checked
                              ? {
                                  branch: "",
                                  department: "",
                                  designation: "",
                                  grade: "",
                                  category: "",
                                  subDepartment: "",
                                  location: "",
                                }
                              : {}),
                          }))
                        }
                      />
                      All employees
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        ["branch", "Branch", branchesList],
                        ["department", "Department", departmentsList],
                        ["designation", "Designation", designationsList],
                        ["grade", "Grade", gradesList],
                        ["category", "Category", categoriesList],
                        ["subDepartment", "Sub-department", subDepartmentsList],
                      ].map(([key, lab, list]) => (
                        <div key={key}>
                          <label className="text-xs font-medium opacity-80">{lab}</label>
                          <select
                            disabled={newPolicy.allEmployees}
                            value={newPolicy[key] || ""}
                            onChange={(e) => setNewPolicy((p) => ({ ...p, [key]: e.target.value }))}
                            className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls} ${
                              newPolicy.allEmployees ? "opacity-50" : ""
                            }`}
                          >
                            <option value="">All</option>
                            {(list || []).map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                      <div>
                        <label className="text-xs font-medium opacity-80">Location</label>
                        <select
                          disabled={newPolicy.allEmployees}
                          value={newPolicy.location || ""}
                          onChange={(e) => setNewPolicy((p) => ({ ...p, location: e.target.value }))}
                          className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls} ${
                            newPolicy.allEmployees ? "opacity-50" : ""
                          }`}
                        >
                          {LOCATION_OPTIONS.map((o) => (
                            <option key={o.value || "all"} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="text-[11px] mt-3 opacity-70">Employment types (empty = all)</p>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex items-center gap-1.5 text-xs">
                          <input
                            type="checkbox"
                            className="rounded"
                            checked={
                              Array.isArray(newPolicy.employmentTypes) &&
                              newPolicy.employmentTypes.includes(opt.value)
                            }
                            onChange={() => toggleEmploymentType(opt.value)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                    <div className="mt-3">
                      <label className="text-xs font-medium opacity-80">
                        Individual employee IDs (comma/space)
                      </label>
                      <input
                        type="text"
                        value={newPolicy.individualEmployeeIds || ""}
                        onChange={(e) =>
                          setNewPolicy((p) => ({ ...p, individualEmployeeIds: e.target.value }))
                        }
                        className={`w-full py-1.5 px-2 border text-sm mt-1 ${inputCls}`}
                        placeholder="e.g. EMP001, EMP002"
                      />
                    </div>
                  </div>
                </>
              )}
            </PolicyFormWizardShell>
            </div>
          </div>
        </div>
        )
      )}
      </>
      )}

      {createModal === "shift" && (
        <div
          className="fixed inset-0 z-[12370] bg-black/50 flex items-center justify-center p-4"
          onClick={closeShiftModal}
        >
          <div
            className="bg-white rounded-md w-[min(96vw,1120px)] h-[min(86vh,860px)] border shadow-xl text-sm text-slate-900 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 border-b border-slate-200 px-5 py-3 flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-slate-900">
                  {editingShiftId ? "Edit shift" : "Create shift"}
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Clock template only (start/end, breaks, flexi). Day marks &amp; penalties belong on Attendance Policy.
                </p>
              </div>
              <button
                type="button"
                onClick={closeShiftModal}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
                aria-label="Close shift form"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-600">Shift name</label>
                  <input
                    placeholder="e.g. General"
                    value={createShiftForm.name}
                    onChange={(e) => setCreateShiftForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full border border-slate-300 p-2 rounded mt-0.5"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Shift type</label>
                  <select
                    value={createShiftForm.shiftType || "fixed"}
                    onChange={(e) =>
                      setCreateShiftForm((f) => ({
                        ...f,
                        shiftType: e.target.value,
                        flexiEnabled: e.target.value === "flexi",
                      }))
                    }
                    className="w-full border p-2 rounded mt-0.5 text-sm"
                  >
                    <option value="fixed">Fixed</option>
                    <option value="flexi">Flexi</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!createShiftForm.isDefault}
                  onChange={(e) =>
                    setCreateShiftForm((f) => ({ ...f, isDefault: e.target.checked }))
                  }
                />
                Set as default shift (applies to all employees unless overridden)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {currentShiftIsFlexi && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">Expected work hours</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={createShiftForm.expectedWorkHours ?? 8}
                      onChange={(e) =>
                        setCreateShiftForm((f) => ({
                          ...f,
                          expectedWorkHours: Number(e.target.value) || 0,
                        }))
                      }
                      className="w-full border p-2 rounded mt-0.5"
                    />
                  </div>
                )}
                <div className="text-[11px] text-slate-500 pt-6">
                  {currentShiftIsFlexi
                    ? "For flexi shifts, set required work hours directly."
                    : "For fixed shifts, work hours are auto-calculated from start/end time."}
                </div>
              </div>

              {!currentShiftIsFlexi && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Start</label>
                      <input
                        type="time"
                        value={createShiftForm.startTime}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({
                            ...f,
                            startTime: e.target.value,
                            expectedWorkHours: computeShiftHours(e.target.value, f.endTime),
                          }))
                        }
                        className="w-full border p-2 rounded mt-0.5"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">End</label>
                      <input
                        type="time"
                        value={createShiftForm.endTime}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({
                            ...f,
                            endTime: e.target.value,
                            expectedWorkHours: computeShiftHours(f.startTime, e.target.value),
                          }))
                        }
                        className="w-full border p-2 rounded mt-0.5"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Total shift hours (auto from start/end)</label>
                      <input
                        type="number"
                        readOnly
                        value={computeShiftHours(createShiftForm.startTime, createShiftForm.endTime)}
                        className="w-full border p-2 rounded mt-0.5 bg-slate-50 text-slate-700"
                      />
                    </div>
                    <div className="text-[11px] text-slate-500 pt-5">
                      Total hours are auto-calculated from start and end time. If end is before start, it is treated as a cross-day shift.
                    </div>
                  </div>
                </>
              )}

              <div className="border border-slate-200 rounded p-2">
                <p className="text-[11px] font-semibold text-slate-700 mb-2">Work hours & break configuration</p>
                <p className="text-[11px] text-slate-500 mb-2">
                  "Full day if &gt;= (h)" means attendance is marked full day when worked hours reach this value.
                  "Half day if &lt; (h)" means attendance becomes half-day when hours fall below this value.
                </p>
                <div className="mb-3 p-2 rounded border border-amber-200 bg-amber-50 text-[11px] text-amber-900">
                  <strong>Ownership:</strong> This shift defines clock times and breaks only.
                  Full-day / half-day / absent hour thresholds and late penalties live on{" "}
                  <em>Attendance Policy</em> (Time rules step) — not here.
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 opacity-60 pointer-events-none" title="Configured on Attendance Policy">
                  {[
                    ["fullDayHours", "Full day (h) — policy"],
                    ["halfDayHours", "Half day (h) — policy"],
                    ["fullDayIfHoursAtLeast", "Full day if ≥ (h) — policy"],
                    ["halfDayIfHoursLessThan", "Half day if &lt; (h) — policy"],
                  ].map(([k, lab]) => (
                    <div key={k}>
                      <label className="text-[10px] text-slate-600">{lab}</label>
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={createShiftForm[k] ?? 0}
                        readOnly
                        className="w-full border p-1.5 rounded text-sm mt-0.5 bg-slate-50"
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Break handling</label>
                    <select
                      value={createShiftForm.breakHandling || "deduct_from_work_hours"}
                      onChange={(e) =>
                        setCreateShiftForm((f) => ({
                          ...f,
                          breakHandling: e.target.value,
                        }))
                      }
                      className="w-full border p-2 rounded mt-0.5 text-sm"
                    >
                      <option value="none">None (do not track break)</option>
                      <option value="deduct_from_work_hours">Do not count break in worked hours</option>
                      <option value="include_in_work_hours">Count break in worked hours</option>
                    </select>
                  </div>
                  {(createShiftForm.breakHandling || "deduct_from_work_hours") !== "none" && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Total break (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={
                            createShiftForm.totalBreakMinutes ??
                            Number(createShiftForm.paidBreakMinutes || 0) +
                              Number(createShiftForm.unpaidBreakMinutes || createShiftForm.breakMinutes || 0)
                          }
                          onChange={(e) =>
                            setCreateShiftForm((f) => ({
                              ...f,
                              totalBreakMinutes: Number(e.target.value) || 0,
                            }))
                          }
                          className="w-full border p-2 rounded mt-0.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Paid break (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={createShiftForm.paidBreakMinutes ?? 0}
                          onChange={(e) =>
                            setCreateShiftForm((f) => ({
                              ...f,
                              paidBreakMinutes: Number(e.target.value) || 0,
                            }))
                          }
                          className="w-full border p-2 rounded mt-0.5"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600">Unpaid break (auto)</label>
                        <input
                          type="number"
                          readOnly
                          value={
                            (createShiftForm.breakHandling || "deduct_from_work_hours") === "deduct_from_work_hours"
                              ? Math.max(
                                  0,
                                  (Number(createShiftForm.totalBreakMinutes) || 0) -
                                    Math.min(
                                      Number(createShiftForm.totalBreakMinutes) || 0,
                                      Number(createShiftForm.paidBreakMinutes) || 0
                                    )
                                )
                              : 0
                          }
                          className="w-full border p-2 rounded mt-0.5 bg-slate-50 text-slate-700"
                        />
                      </div>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  {(createShiftForm.breakHandling || "deduct_from_work_hours") === "none"
                    ? "Break is not tracked for this shift."
                    : "If break is not counted in worked hours, employees must stay longer to complete required work hours."}
                </p>
              </div>

              {!currentShiftIsFlexi && (
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={!!createShiftForm.crossDayShift}
                    onChange={(e) =>
                      setCreateShiftForm((f) => ({ ...f, crossDayShift: e.target.checked }))
                    }
                  />
                  Cross-day shift
                </label>
              )}
              {!currentShiftIsFlexi && (
                <p className="text-[11px] text-slate-500 -mt-2">
                  Cross-day means shift starts on one day and ends on the next day (example: 10:00 PM to 06:00 AM).
                </p>
              )}

              {currentShiftIsFlexi && (
                <div className="border border-slate-200 rounded p-2">
                  <p className="text-[11px] font-semibold text-slate-700 mb-2">Flexi bandwidth & core hours</p>
                  <p className="text-[11px] text-slate-500 mb-2">
                    Use this when employees can check in within a window (bandwidth), but must be present during a mandatory
                    overlap window (core hours). For fixed shifts, keep this disabled.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-600">Bandwidth start</label>
                      <input
                        type="time"
                        value={createShiftForm.flexiBandwidthStart || "08:00"}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({ ...f, flexiBandwidthStart: e.target.value }))
                        }
                        className="w-full border p-1.5 rounded text-sm mt-0.5"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-600">Bandwidth end</label>
                      <input
                        type="time"
                        value={createShiftForm.flexiBandwidthEnd || "11:00"}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({ ...f, flexiBandwidthEnd: e.target.value }))
                        }
                        className="w-full border p-1.5 rounded text-sm mt-0.5"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-600">Core start</label>
                      <input
                        type="time"
                        value={createShiftForm.coreHoursStart || "11:00"}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({ ...f, coreHoursStart: e.target.value }))
                        }
                        className="w-full border p-1.5 rounded text-sm mt-0.5"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-600">Core end</label>
                      <input
                        type="time"
                        value={createShiftForm.coreHoursEnd || "17:00"}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({ ...f, coreHoursEnd: e.target.value }))
                        }
                        className="w-full border p-1.5 rounded text-sm mt-0.5"
                      />
                    </div>
                  </div>
                </div>
              )}
              {!currentShiftIsFlexi && (
                <div className="border border-slate-200 rounded p-2">
                  <p className="text-[11px] font-semibold text-slate-700 mb-2">Shift rotation</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!createShiftForm.shiftRotationEnabled}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({ ...f, shiftRotationEnabled: e.target.checked }))
                        }
                      />
                      Enable rotation for this shift
                    </label>
                    <div>
                      <label className="text-[10px] text-slate-600">Rotation pattern</label>
                      <select
                        value={createShiftForm.shiftRotationPattern || "weekly"}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({ ...f, shiftRotationPattern: e.target.value }))
                        }
                        className="w-full border p-1.5 rounded text-sm mt-0.5"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="fortnightly">Fortnightly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-2 text-xs mt-5">
                      <input
                        type="checkbox"
                        checked={!!createShiftForm.shiftRotationRequiresApproval}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({
                            ...f,
                            shiftRotationRequiresApproval: e.target.checked,
                          }))
                        }
                      />
                      Rotation requires approval
                    </label>
                  </div>
                </div>
              )}

              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Shift assignment override</p>
                <label className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={!!createShiftForm.allowShiftOverride}
                    onChange={(e) =>
                      setCreateShiftForm((f) => ({ ...f, allowShiftOverride: e.target.checked }))
                    }
                  />
                  Allow employees / managers to override assigned shift (roster change)
                </label>
                {createShiftForm.allowShiftOverride && (
                  <>
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={!!createShiftForm.shiftOverrideRequiresApproval}
                        onChange={(e) =>
                          setCreateShiftForm((f) => ({
                            ...f,
                            shiftOverrideRequiresApproval: e.target.checked,
                          }))
                        }
                      />
                      Override requires approval workflow
                    </label>
                    {createShiftForm.shiftOverrideRequiresApproval && (
                      <div className="space-y-2 pl-1 border-l-2 border-redhalo-teal/50">
                        <p className="text-[11px] text-slate-600">Approval steps for shift override</p>
                        {(createShiftForm.shiftOverrideWorkflowSteps || []).map((step, idx) => (
                          <div key={idx} className="flex flex-wrap gap-2 items-center">
                            <select
                              value={resolveRoleSelectValue(WORKFLOW_ROLES, step.role)}
                              onChange={(e) =>
                                updateShiftOverrideWorkflowStep(idx, "role", e.target.value)
                              }
                              className="border p-1.5 rounded text-sm"
                              aria-label={`Shift override step ${idx + 1} role`}
                            >
                              <PolicyRoleSelectOptions
                                roleOptions={WORKFLOW_ROLES}
                                storedValue={step.role}
                              />
                            </select>
                            <label className="flex items-center gap-1 text-xs">
                              <input
                                type="checkbox"
                                checked={!!step.notifyOnly}
                                onChange={(e) =>
                                  updateShiftOverrideWorkflowStep(idx, "notifyOnly", e.target.checked)
                                }
                              />
                              Notify only
                            </label>
                            {!step.notifyOnly && (
                              <>
                                <span className="text-xs">SLA</span>
                                <input
                                  type="number"
                                  min={1}
                                  className="w-14 border p-1 rounded text-sm"
                                  value={step.slaDays ?? 3}
                                  onChange={(e) =>
                                    updateShiftOverrideWorkflowStep(
                                      idx,
                                      "slaDays",
                                      Number(e.target.value) || 3
                                    )
                                  }
                                />
                              </>
                            )}
                            <button
                              type="button"
                              className="text-red-600 text-xs"
                              onClick={() => removeShiftOverrideWorkflowStep(idx)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="text-redhalo-navy text-sm"
                          onClick={addShiftOverrideWorkflowStep}
                        >
                          + Add step
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-white">
              <button
                type="button"
                className="px-3 py-2 border rounded"
                onClick={closeShiftModal}
              >
                Cancel
              </button>
              <button type="button" className="px-3 py-2 rh-btn-primary rounded" onClick={addShift}>
                {editingShiftId ? "Save shift" : "Create shift"}
              </button>
            </div>
          </div>
        </div>
      )}
      {createModal === "leave" && (
        <div className="fixed inset-0 z-[12370] bg-black/50 flex items-center justify-center p-4" onClick={() => setCreateModal(null)}>
          <div className="bg-white p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-semibold mb-3">Create leave group</h4>
            <input
              className="w-full border p-2 mb-2"
              placeholder="Name"
              value={createLeaveForm.name}
              onChange={(e) => setCreateLeaveForm((f) => ({ ...f, name: e.target.value }))}
            />
            <input
              className="w-full border p-2"
              placeholder="Description"
              value={createLeaveForm.description}
              onChange={(e) => setCreateLeaveForm((f) => ({ ...f, description: e.target.value }))}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="px-3 py-2 border" onClick={() => setCreateModal(null)}>
                Cancel
              </button>
              <button type="button" className="px-3 py-2 rh-btn-primary" onClick={addLeaveGroup}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}
      {createModal === "weeklyoff" && (
        <div className="fixed inset-0 z-[12370] bg-black/50 flex items-center justify-center p-4" onClick={() => setCreateModal(null)}>
          <div className="bg-white p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-semibold mb-3">Create weekly off group</h4>
            <input
              className="w-full border p-2 mb-2"
              placeholder="Name"
              value={createWeeklyOffForm.name}
              onChange={(e) => setCreateWeeklyOffForm((f) => ({ ...f, name: e.target.value }))}
            />
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((d) => (
                <label key={d.value} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={createWeeklyOffForm.offDays.includes(d.value)}
                    onChange={() => toggleWeeklyOffDay(d.value)}
                  />
                  {d.label.slice(0, 3)}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="px-3 py-2 border" onClick={() => setCreateModal(null)}>
                Cancel
              </button>
              <button type="button" className="px-3 py-2 rh-btn-primary" onClick={addWeeklyOffGroup}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}
      {createModal === "holiday" && (
        <div className="fixed inset-0 z-[12370] bg-black/50 flex items-center justify-center p-4" onClick={() => setCreateModal(null)}>
          <div className="bg-white p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-semibold mb-3">Create holiday group</h4>
            <input
              className="w-full border p-2 mb-2"
              placeholder="Group name"
              value={createHolidayForm.name}
              onChange={(e) => setCreateHolidayForm((f) => ({ ...f, name: e.target.value }))}
            />
            {(createHolidayForm.holidays || []).map((h, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="date"
                  value={h.date || ""}
                  onChange={(e) => updateHolidayInForm(idx, "date", e.target.value)}
                  className="border p-1 text-sm"
                />
                <input
                  placeholder="Holiday name"
                  value={h.name || ""}
                  onChange={(e) => updateHolidayInForm(idx, "name", e.target.value)}
                  className="flex-1 border p-1 text-sm"
                />
                <button type="button" className="text-red-600 text-xs" onClick={() => removeHolidayFromForm(idx)}>
                  ×
                </button>
              </div>
            ))}
            <button type="button" className="text-redhalo-navy text-sm mb-2" onClick={addHolidayToForm}>
              + Add holiday
            </button>
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" className="px-3 py-2 border" onClick={() => setCreateModal(null)}>
                Cancel
              </button>
              <button type="button" className="px-3 py-2 rh-btn-primary" onClick={addHolidayGroup}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {portalToBody(
        saveGateModal?.type === "effective_date" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[12380] p-4">
          <div className="w-[min(100%,480px)] bg-white border shadow-xl p-6 rounded-none">
            <h3 className="text-lg font-semibold text-amber-900">Effective date must be updated</h3>
            <p className="text-sm text-slate-700 mt-3">
              Policy content changed but the effective date is unchanged. Set a new effective date (often future), then save again.
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm bg-amber-700 text-white"
                onClick={() => setSaveGateModal(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
        )
      )}
      {portalToBody(
        saveGateModal?.type === "overlap" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[12380] p-4">
          <div className="w-[min(100%,560px)] max-h-[85vh] bg-white border shadow-xl flex flex-col rounded-none">
            <div className="px-5 py-4 border-b bg-amber-50">
              <h3 className="text-base font-semibold">Applicability overlap</h3>
              <p className="text-sm text-slate-700 mt-2">
                Another active policy may cover the same scope. You can go back to edit or continue to preview and save.
              </p>
            </div>
            <div className="px-5 py-3 overflow-y-auto flex-1">
              <ul className="space-y-2 text-sm">
                {saveGateModal.items.map((item) => (
                  <li key={item.id} className="border p-3 bg-slate-50">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-xs text-slate-600 mt-1">{item.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <button type="button" className="px-4 py-2 border text-sm" onClick={() => setSaveGateModal(null)}>
                Back to edit
              </button>
              <button
                type="button"
                className="px-4 py-2 rh-btn-primary text-sm"
                onClick={continueAfterOverlapAck}
              >
                Continue to preview &amp; save
              </button>
            </div>
          </div>
        </div>
        )
      )}

      {portalToBody(
        showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[12380] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-white shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Policy preview</h3>
              <button type="button" onClick={() => setShowSummaryModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto text-sm">
              {getPolicySummaryRows(newPolicy).map((row, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 border-b py-2">
                  <span className="font-medium text-slate-700">{row.label}</span>
                  <span className="col-span-2">{String(row.value ?? "—")}</span>
                </div>
              ))}
              <p className="mt-3 text-sm text-slate-600">
                <strong>Roles that can approve:</strong> {selectedApprovalRoleLabels.join(", ") || "—"}
              </p>
            </div>
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm bg-gray-200"
                onClick={() => setShowSummaryModal(false)}
              >
                Back
              </button>
              <button type="button" className="px-4 py-2 text-sm rh-btn-primary" onClick={handleSavePolicy}>
                Save policy
              </button>
            </div>
          </div>
        </div>
        )
      )}

      {portalToBody(
        previewPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[12380] p-4">
          <div className="w-[780px] max-w-[95vw] max-h-[85vh] bg-white shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b flex justify-between">
              <h3 className="font-semibold">Saved policy preview</h3>
              <button type="button" onClick={() => setPreviewPolicy(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-auto text-sm">
              {getPolicySummaryRows(previewPolicy).map((row, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 border-b py-2">
                  <span className="font-medium">{row.label}</span>
                  <span className="col-span-2">{String(row.value ?? "—")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        )
      )}

      {portalToBody(
        flowchartPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[12380] p-4">
          <div className="w-[min(96vw,1120px)] max-h-[90vh] bg-white rounded-xl shadow-2xl border flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-sm">Flowchart — {flowchartPolicy.policyName || "Policy"}</h3>
                <p className="text-[11px] text-slate-500">Print or save as PDF from the print dialog.</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => printCombinedFlowchartPdf(flowchartPolicy)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-800 text-white rounded-lg"
                >
                  <Printer size={14} /> Print
                </button>
                <button type="button" onClick={() => setFlowchartPolicy(null)}>
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto">
              <div dangerouslySetInnerHTML={{ __html: buildAttendanceProcessSvg(flowchartPolicy) }} />
            </div>
          </div>
        </div>
        )
      )}

      {shiftActionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[12360]" aria-hidden onClick={() => setShiftActionMenu(null)} />
            <div
              className="fixed z-[12361] w-36 bg-white border border-slate-200 shadow-md rounded-sm py-1 text-xs text-left"
              style={{ top: shiftActionMenu.top, left: shiftActionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => openEditShift(shiftActionMenu.shift)}
                className="w-full px-3 py-1.5 hover:bg-slate-50 text-left"
              >
                Edit
              </button>
              {(shiftActionMenu.shift.status || "active") === "active" ? (
                <button
                  type="button"
                  onClick={() => deactivateShift(shiftActionMenu.shift.id)}
                  className="w-full px-3 py-1.5 hover:bg-slate-50 text-left"
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => activateShift(shiftActionMenu.shift.id)}
                  className="w-full px-3 py-1.5 hover:bg-slate-50 text-left"
                >
                  Activate
                </button>
              )}
              <button
                type="button"
                onClick={() => deleteShift(shiftActionMenu.shift.id)}
                className="w-full px-3 py-1.5 hover:bg-red-50 text-red-600 text-left"
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}

      {actionMenu &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[12360]" aria-hidden onClick={() => setActionMenu(null)} />
            <div
              className="fixed z-[12361] w-56 rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-[#3C3C4C] dark:bg-[#1D1D1D]"
              style={{ top: actionMenu.top, left: actionMenu.left }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  openEditForm(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  setPreviewPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Preview (configuration)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  setFlowchartPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Flowchart
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => openPolicyDocumentPreview(actionMenu.policy)}
              >
                Policy document (HR)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  printPolicyDocument(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Print (summary)
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-redhalo-navy-dark hover:bg-gray-50 dark:text-redhalo-mint dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  if (window.confirm("Publish this policy document to covered employees?")) {
                    handlePushPolicyDocument(actionMenu.policy);
                  }
                }}
              >
                Push policy document
              </button>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  handleCopyPolicy(actionMenu.policy);
                  setActionMenu(null);
                }}
              >
                Copy
              </button>
              {actionMenu.policy.status === "active" ? (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-[#3C3C3C]"
                  onClick={() => {
                    handleDeactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-emerald-800 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-[#3C3C3C]"
                  onClick={() => {
                    handleReactivatePolicy(actionMenu.policy.id);
                    setActionMenu(null);
                  }}
                >
                  Reactivate
                </button>
              )}
              <button
                type="button"
                className="w-full border-t border-gray-200 px-3 py-2 text-left text-redhalo-red hover:bg-rose-50 dark:border-[#3C3C3C] dark:text-rose-400 dark:hover:bg-[#3C3C3C]"
                onClick={() => {
                  handleDeletePolicy(actionMenu.policy.id);
                  setActionMenu(null);
                }}
              >
                Delete
              </button>
            </div>
          </>,
          document.body
        )}

      {portalToBody(
        policyDocModal &&
        (() => {
          const docPolicy = policies.find((p) => p.id === policyDocModal.id) || policyDocModal;
          const docHtml = buildAttendancePolicyDocumentHtml(docPolicy, policyDocumentOptions);
          return (
            <div className="fixed inset-0 z-[12380] flex items-center justify-center p-4 bg-black/50">
              <div className="w-[min(96vw,880px)] max-h-[92vh] bg-white flex flex-col border shadow-lg overflow-hidden">
                <div className="px-4 py-3 border-b flex justify-between bg-slate-50">
                  <div>
                    <h3 className="font-semibold text-sm">Policy document</h3>
                    <p className="text-xs text-slate-600 mt-1">{docPolicy.policyName || "Policy"}</p>
                    <p className="text-[11px] text-slate-500">
                      v{docPolicy.policyVersion ?? 1} · Effective {docPolicy.effectiveDate || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyDocModal(null);
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden bg-slate-100 p-2">
                  <iframe title="Policy document" className="w-full h-[min(70vh,640px)] border bg-white" srcDoc={docHtml} />
                </div>
                <div className="px-4 py-3 border-t flex flex-wrap gap-2 justify-between bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyHistoryModalId(docPolicy.id);
                      setHistoryPreviewEntry(null);
                    }}
                    className="px-3 py-1.5 text-xs border"
                  >
                    View history
                  </button>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => printPolicyFullDocument(docPolicy)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border"
                    >
                      <Printer size={14} /> Print document
                    </button>
                    {!docPolicy.policyDocumentPushed ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm("Publish to covered employees?")) handlePushPolicyDocument(docPolicy);
                        }}
                        className="px-3 py-1.5 text-xs rh-btn-primary"
                      >
                        Push to employees
                      </button>
                    ) : (
                      <span className="text-xs text-slate-600 self-center">Published</span>
                    )}
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs border"
                      onClick={() => {
                        setPolicyDocModal(null);
                        setPolicyHistoryModalId(null);
                        setHistoryPreviewEntry(null);
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {portalToBody(
        policyHistoryModalId &&
        (() => {
          const histPolicy = policies.find((p) => p.id === policyHistoryModalId);
          if (!histPolicy) return null;
          const rows = getPolicySnapshotsForDisplay(histPolicy);
          const previewHtml = historyPreviewEntry
            ? buildAttendancePolicyDocumentHtml(historyPreviewEntry.policy, policyDocumentOptions)
            : "";
          return (
            <div className="fixed inset-0 z-[12390] flex items-center justify-center p-4 bg-black/55">
              <div className="w-[min(96vw,900px)] max-h-[92vh] bg-white border shadow-xl flex flex-col overflow-hidden">
                <div className="px-4 py-3 border-b flex justify-between bg-slate-50">
                  <div>
                    <h3 className="font-semibold text-sm">Policy version history</h3>
                    <p className="text-xs text-slate-600">{histPolicy.policyName || "Policy"}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4">
                  <table className="w-full text-left text-xs border">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="px-2 py-2">Version</th>
                        <th className="px-2 py-2">Effective</th>
                        <th className="px-2 py-2">Recorded</th>
                        <th className="px-2 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={`${row.version}-${row.savedAt}`} className="border-b">
                          <td className="px-2 py-2">{row.version}</td>
                          <td className="px-2 py-2">{row.effectiveDate || "—"}</td>
                          <td className="px-2 py-2">
                            {row.savedAt ? new Date(row.savedAt).toLocaleString() : "—"}
                          </td>
                          <td className="px-2 py-2 text-right">
                            <button
                              type="button"
                              className="text-redhalo-navy mr-2"
                              onClick={() => setHistoryPreviewEntry(row)}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              className="text-slate-800"
                              onClick={() => printPolicySnapshotDocument(row.policy)}
                            >
                              Print
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {historyPreviewEntry && (
                    <div className="mt-4 border p-2 bg-slate-50">
                      <p className="text-xs font-semibold mb-2">
                        Preview v{historyPreviewEntry.version}
                      </p>
                      <iframe title="Version preview" className="w-full h-[min(50vh,480px)] border bg-white" srcDoc={previewHtml} />
                    </div>
                  )}
                </div>
                <div className="px-4 py-3 border-t flex justify-end">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs border"
                    onClick={() => {
                      setPolicyHistoryModalId(null);
                      setHistoryPreviewEntry(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
};

export default AttendancePolicyComponent;
